import { useRef } from "react";
import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "./api";
import endpoints, { adminEndpoints, clinicEndpoints } from "./endpoints";
import { pollingOptions, buildUrlWithParams } from "./queryHelpers";
import { setAccessToken, setRefreshToken, setStoredUser } from "@utils/cookies";

// Local calendar date as "YYYY-MM-DD". Deliberately not UTC: "today" has to
// mean the user's today, the same day the cash-flow grid marks as current.
function todayISO() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export const useRegister = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (credentials) => {
      // Send all registration fields to backend
      const payload = {
        email: credentials.email,
        first_name: credentials.first_name,
        last_name: credentials.last_name,
        password: credentials.password,
        password_confirm: credentials.password_confirm,
      };
      const response = await api.post(endpoints.AUTH_REGISTER, payload);
      return response.data;
    },
    onSuccess: (data) => {
      // Store tokens and user info
      if (data.access) setAccessToken(data.access);
      if (data.refresh) setRefreshToken(data.refresh);
      if (data.user) setStoredUser(data.user);

      queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });
};

export const useLogin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (credentials) => {
      const response = await api.post(endpoints.AUTH_LOGIN, credentials);
      return response.data;
    },
    onSuccess: (data) => {
      // Store tokens and user info
      if (data.access) setAccessToken(data.access);
      if (data.refresh) setRefreshToken(data.refresh);
      if (data.user) setStoredUser(data.user);

      queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });
};

// Server's current view of the logged-in user. Re-checked on focus and every
// 5 min so a role/clinic change made by an organizer is noticed mid-session.
export const useCurrentUser = (options = {}) => {
  return useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      const response = await api.get(endpoints.AUTH_ME);
      return response.data;
    },
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: false,
    ...options,
  });
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: async (passwordData) => {
      const response = await api.post(endpoints.AUTH_CHANGE_PASSWORD, passwordData);
      return response.data;
    },
  });
};

export const usePasswordResetRequest = () => {
  return useMutation({
    mutationFn: async (emailData) => {
      const response = await api.post(endpoints.AUTH_PASSWORD_RESET_REQUEST, emailData);
      return response.data;
    },
  });
};

export const usePasswordResetVerify = () => {
  return useMutation({
    mutationFn: async (tokenData) => {
      const response = await api.post(endpoints.AUTH_PASSWORD_RESET_VERIFY, tokenData);
      return response.data;
    },
  });
};

export const usePasswordResetConfirm = () => {
  return useMutation({
    mutationFn: async (resetData) => {
      const response = await api.post(endpoints.AUTH_PASSWORD_RESET_CONFIRM, resetData);
      return response.data;
    },
  });
};

// ==================== USERS ====================

export const useUsers = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["users", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_ALL_USERS, filters, { clean: true });
      const response = await api.get(url);
      return response.data;
    },
    enabled: false, // Don't fetch automatically, only when explicitly needed
    ...options,
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch(endpoints.UPDATE_USER(id), data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["users", data.id] });
    },
  });
};

// ==================== LAB INVOICES ====================

export const useLabInvoiceAttachments = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["lab-invoices", "attachments", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_LAB_INVOICE_ATTACHMENTS, filters, {
        clean: true,
      });
      const response = await api.get(url);
      return response.data;
    },
    enabled: Boolean(
      filters.clinic &&
      (filters.date ||
        filters.assigned_date ||
        filters.effective_date ||
        filters.effective_month ||
        filters.status ||
        filters.assigned !== undefined),
    ),
    ...options,
  });
};

export const useLabInvoiceReviewAttachments = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["lab-invoices", "review", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_LAB_INVOICE_REVIEW_ATTACHMENTS, filters, {
        clean: true,
      });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

export const useLabInvoiceClinicOverview = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["lab-invoices", "clinic-overview", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_LAB_INVOICE_CLINIC_OVERVIEW, filters, {
        clean: true,
      });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

export const useAssignedLabInvoiceDownloads = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["lab-invoices", "assigned-downloads", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_LAB_INVOICE_ASSIGNED_DOWNLOADS, filters, {
        clean: true,
      });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

export const useUpdateLabInvoiceAttachmentAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ attachmentId, data }) => {
      const response = await api.patch(
        endpoints.UPDATE_LAB_INVOICE_ATTACHMENT_ASSIGNMENT(attachmentId),
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lab-invoices"] });
    },
  });
};

export const useUpdateLabInvoiceAttachmentPdfEdits = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ attachmentId, data }) => {
      const response = await api.patch(
        endpoints.UPDATE_LAB_INVOICE_ATTACHMENT_PDF_EDITS(attachmentId),
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lab-invoices"] });
    },
  });
};

export const useReviewLabInvoiceBreakdown = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ attachmentId, breakdownId }) => {
      const response = await api.post(
        endpoints.REVIEW_LAB_INVOICE_BREAKDOWN(attachmentId, breakdownId),
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lab-invoices"] });
    },
  });
};

export const useRequestLabInvoiceAttachmentReview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (attachmentId) => {
      const response = await api.post(
        endpoints.REQUEST_LAB_INVOICE_ATTACHMENT_REVIEW(attachmentId),
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lab-invoices"] });
    },
  });
};

export const useRescanLabInvoiceAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (attachmentId) => {
      const response = await api.post(endpoints.RESCAN_LAB_INVOICE_ATTACHMENT(attachmentId));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lab-invoices"] });
    },
  });
};

export const useDiscardLabInvoiceAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (attachmentId) => {
      const response = await api.post(endpoints.DISCARD_LAB_INVOICE_ATTACHMENT(attachmentId));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lab-invoices"] });
    },
  });
};

export const useRetryLabInvoiceAttachmentFromEmail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (attachmentId) => {
      const response = await api.post(
        endpoints.RETRY_LAB_INVOICE_ATTACHMENT_FROM_EMAIL(attachmentId),
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lab-invoices"] });
    },
  });
};

export const useClaimLabInvoiceAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ attachmentId, clinic }) => {
      const response = await api.post(endpoints.CLAIM_LAB_INVOICE_ATTACHMENT(attachmentId), {
        clinic,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lab-invoices"] });
    },
  });
};

export const useUploadLabInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, clinic }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("clinic", clinic);
      const response = await api.post(endpoints.UPLOAD_LAB_INVOICE, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lab-invoices"] });
    },
  });
};

function getSignedUrlRefreshDelayMs(data) {
  const refreshAfterSeconds = Number(data?.refresh_after_seconds);
  if (Number.isFinite(refreshAfterSeconds) && refreshAfterSeconds > 0) {
    return Math.max(30, refreshAfterSeconds) * 1000;
  }

  const expiresInSeconds = Number(data?.expires_in_seconds);
  if (Number.isFinite(expiresInSeconds) && expiresInSeconds > 90) {
    return Math.max(30, expiresInSeconds - 60) * 1000;
  }

  return 10 * 60 * 1000;
}

export const useLabInvoiceAttachmentViewUrl = (attachmentId, options = {}) => {
  const { enabled: enabledOption = true, ...queryOptions } = options;

  return useQuery({
    queryKey: ["lab-invoices", "attachments", attachmentId, "view-url"],
    queryFn: async () => {
      const response = await api.get(endpoints.GET_LAB_INVOICE_ATTACHMENT_VIEW_URL(attachmentId));
      return response.data;
    },
    enabled: Boolean(attachmentId) && enabledOption,
    staleTime: (query) => getSignedUrlRefreshDelayMs(query.state.data),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...queryOptions,
  });
};

// ==================== DAILY REPORT FILES ====================
// Plain file attachments on a clinic-day, no lab-cost link — see
// apps/report_files on the backend. Uploads are also read for insurance EFTs
// in the background (useInsuranceEftDay below).

// `period: "month"` lists the Monthly Reconciliation's files, filed under the
// first of the month and kept apart from that day's own files.
export const useReportFiles = ({ clinic, date, period = "day" }, options = {}) => {
  const { enabled: enabledOption = true, ...queryOptions } = options;
  return useQuery({
    queryKey: ["report-files", clinic, date, period],
    queryFn: async () => {
      const response = await api.get(endpoints.REPORT_FILES, {
        params: { clinic, date, period },
      });
      return response.data;
    },
    enabled: Boolean(clinic) && Boolean(date) && enabledOption,
    ...queryOptions,
  });
};

export const useUploadReportFiles = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ files, clinic, date, period = "day" }) => {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      formData.append("clinic", clinic);
      formData.append("date", date);
      formData.append("period", period);
      const response = await api.post(endpoints.REPORT_FILES, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["report-files", variables.clinic, variables.date],
      });
      // New files are queued for an EFT read; refetching starts the polling.
      queryClient.invalidateQueries({
        queryKey: ["insurance-eft", variables.clinic, variables.date],
      });
    },
  });
};

export const useDeleteReportFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ fileId }) => {
      await api.delete(endpoints.DELETE_REPORT_FILE(fileId));
      return fileId;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["report-files", variables.clinic, variables.date],
      });
      // A deleted file's EFT total drops out of the day's suggestion.
      queryClient.invalidateQueries({
        queryKey: ["insurance-eft", variables.clinic, variables.date],
      });
    },
  });
};

// A read normally finishes in ~10 s, so poll quickly at first, then back off,
// then give up: a file still waiting after 5 minutes means the worker is not
// running, and polling would otherwise go on forever. A page refresh or a new
// upload/rescan starts polling again.
function insuranceEftPollInterval(data) {
  if (!(data?.pending_count > 0)) return false;
  const waited = data.oldest_pending_seconds ?? 0;
  if (waited < 60) return 3000;
  if (waited < 300) return 15000;
  return false;
}

/**
 * Insurance EFT readings for one clinic-day: `suggested_total` (null when no
 * file had any) plus one entry per file in `scans`, keyed by `report_file_id`.
 * One request covers every file of the day; it polls only while a file is
 * still queued or being read (see insuranceEftPollInterval).
 */
export const useInsuranceEftDay = ({ clinic, date }, options = {}) => {
  const { enabled: enabledOption = true, ...queryOptions } = options;
  return useQuery({
    queryKey: ["insurance-eft", clinic, date],
    queryFn: async () => {
      const response = await api.get(endpoints.INSURANCE_EFT_DAY, { params: { clinic, date } });
      return response.data;
    },
    enabled: Boolean(clinic) && Boolean(date) && enabledOption,
    refetchInterval: (query) => insuranceEftPollInterval(query.state.data),
    ...queryOptions,
  });
};

/**
 * Individual insurance EFTs and cheques read from one clinic's uploaded files
 * over a date range (counted files only — the same set the Deposit Sheet modal
 * lists). Returns { results: [{ key, date, kind, field, payer, reference,
 * amount, needs_review }], days_with_files: { <field>: [dates] } }.
 */
export const useInsurancePayments = ({ clinic, date_from, date_to } = {}, options = {}) =>
  useQuery({
    queryKey: ["insurance-eft", "payments", clinic, date_from, date_to],
    queryFn: async () => {
      const response = await api.get(endpoints.INSURANCE_EFT_PAYMENTS, {
        params: { clinic, date_from, date_to },
      });
      return response.data;
    },
    ...options,
    enabled: (options.enabled ?? true) && !!clinic && !!date_from && !!date_to,
  });

/**
 * Per clinic-day insurance EFT totals for several clinics over a date range, in
 * one request: `results[clinic][date] = { total, cheque_total, needs_review, pending }`. Only
 * days with read files appear. Used to tint the Deposit Sheet's Insurance EFT
 * cells without one day-endpoint call per cell.
 */
export const useMultiClinicInsuranceEftTotals = (clinics = [], range = {}, options = {}) => {
  const sorted = Array.from(new Set((clinics || []).filter(Boolean)))
    .map((c) => String(c).toUpperCase())
    .sort();
  const { date_from: dateFrom, date_to: dateTo } = range;
  const { enabled: enabledOption = true, ...queryOptions } = options;
  return useQuery({
    queryKey: ["insurance-eft", "totals", sorted, dateFrom, dateTo],
    queryFn: async () => {
      const response = await api.get(endpoints.INSURANCE_EFT_TOTALS, {
        params: { clinics: sorted.join(","), date_from: dateFrom, date_to: dateTo },
      });
      return response.data;
    },
    enabled: enabledOption && sorted.length > 0 && Boolean(dateFrom) && Boolean(dateTo),
    ...queryOptions,
  });
};

/** `useMultiClinicInsuranceEftTotals`, one cached request per month range. */
export const useMultiClinicInsuranceEftTotalsForMonths = (
  clinics = [],
  ranges = [],
  options = {},
) => {
  const sorted = Array.from(new Set((clinics || []).filter(Boolean)))
    .map((c) => String(c).toUpperCase())
    .sort();
  const { enabled: enabledOption = true } = options;
  return useQueries({
    queries: ranges.map(({ from: dateFrom, to: dateTo }) => ({
      queryKey: ["insurance-eft", "totals", sorted, dateFrom, dateTo],
      queryFn: async () => {
        const response = await api.get(endpoints.INSURANCE_EFT_TOTALS, {
          params: { clinics: sorted.join(","), date_from: dateFrom, date_to: dateTo },
        });
        return response.data;
      },
      enabled: enabledOption && sorted.length > 0 && Boolean(dateFrom) && Boolean(dateTo),
    })),
    combine: combineResultsByClinic,
  });
};

export const useRescanInsuranceEft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ fileId }) => {
      const response = await api.post(endpoints.RESCAN_INSURANCE_EFT(fileId));
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["insurance-eft", variables.clinic, variables.date],
      });
    },
  });
};

export const useReportFileViewUrl = (fileId, options = {}) => {
  const { enabled: enabledOption = true, ...queryOptions } = options;
  return useQuery({
    queryKey: ["report-files", "view-url", fileId],
    queryFn: async () => {
      const response = await api.get(endpoints.GET_REPORT_FILE_VIEW_URL(fileId));
      return response.data;
    },
    enabled: Boolean(fileId) && enabledOption,
    // Signed URLs expire; refresh shortly before they do rather than on focus.
    staleTime: (query) => getSignedUrlRefreshDelayMs(query.state.data),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...queryOptions,
  });
};

// ==================== INVISALIGN INVOICES ====================
// Invisalign invoice PDFs on a clinic-day (apps/invisalign_invoices). Read on
// upload for amount + doctor; the Performance Report sums them per practitioner
// as "Suggested:" under Invisalign Costs.

export const useInvisalignInvoices = ({ clinic, date }, options = {}) => {
  const { enabled: enabledOption = true, ...queryOptions } = options;
  return useQuery({
    queryKey: ["invisalign-invoices", clinic, date],
    queryFn: async () => {
      const response = await api.get(endpoints.INVISALIGN_INVOICES, { params: { clinic, date } });
      return response.data;
    },
    enabled: Boolean(clinic) && Boolean(date) && enabledOption,
    ...queryOptions,
  });
};

export const useUploadInvisalignInvoices = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ files, clinic, date }) => {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      formData.append("clinic", clinic);
      formData.append("date", date);
      const response = await api.post(endpoints.INVISALIGN_INVOICES, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },
    // Each invoice is filed under the clinic and Invoice Date printed on it, not
    // the page's, so any clinic-day may have changed.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invisalign-invoices"] });
    },
  });
};

export const useDeleteInvisalignInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ fileId }) => {
      await api.delete(endpoints.DELETE_INVISALIGN_INVOICE(fileId));
      return fileId;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["invisalign-invoices", variables.clinic, variables.date],
      });
      // The Admin review list and overview span every clinic-day.
      queryClient.invalidateQueries({ queryKey: ["invisalign-invoices", "review"] });
      queryClient.invalidateQueries({ queryKey: ["invisalign-invoices", "clinic-overview"] });
    },
  });
};

// Admin review: every Invisalign invoice across the user's clinics, paginated.
export const useInvisalignInvoiceReview = (filters = {}, options = {}) =>
  useQuery({
    queryKey: ["invisalign-invoices", "review", filters],
    queryFn: async () => {
      const response = await api.get(endpoints.GET_INVISALIGN_INVOICE_REVIEW, { params: filters });
      return response.data;
    },
    placeholderData: (previous) => previous,
    ...options,
  });

// Admin overview: per-clinic counts and amounts, optional ?month=YYYY-MM (invoice date).
export const useInvisalignInvoiceClinicOverview = (filters = {}, options = {}) =>
  useQuery({
    queryKey: ["invisalign-invoices", "clinic-overview", filters],
    queryFn: async () => {
      const response = await api.get(endpoints.GET_INVISALIGN_INVOICE_CLINIC_OVERVIEW, {
        params: filters,
      });
      return response.data;
    },
    ...options,
  });

export const useInvisalignInvoiceViewUrl = (fileId, options = {}) => {
  const { enabled: enabledOption = true, ...queryOptions } = options;
  return useQuery({
    queryKey: ["invisalign-invoices", "view-url", fileId],
    queryFn: async () => {
      const response = await api.get(endpoints.GET_INVISALIGN_INVOICE_VIEW_URL(fileId));
      return response.data;
    },
    enabled: Boolean(fileId) && enabledOption,
    staleTime: (query) => getSignedUrlRefreshDelayMs(query.state.data),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...queryOptions,
  });
};

// ==================== REPORTS ====================

export const useCreateReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clinicId, data }) => {
      const response = await api.post(endpoints.CREATE_REPORT(clinicId), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reports"] });
      queryClient.invalidateQueries({ queryKey: ["lab-invoices"] });
    },
  });
};

export const useUpdateReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clinicId, id, data }) => {
      const response = await api.patch(endpoints.UPDATE_REPORT(clinicId, id), data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["reports"] });
      queryClient.invalidateQueries({ queryKey: ["reports", data.id] });
      queryClient.invalidateQueries({ queryKey: ["lab-invoices"] });
    },
  });
};

export const useReportClinicData = (clinic, filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["reports", "clinic", clinic, filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_REPORT_CLINIC_DATA(clinic), filters);
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!clinic,
    ...options,
  });
};

export const useReportTotal = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["reports", "total", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_REPORT_TOTAL, filters);
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!filters.clinic && !!filters.date, // Only fetch when clinic and date are provided
    ...options,
  });
};

export const useReportTotalMonthPractitioners = (
  { clinic, month, practitioners } = {},
  options = {},
) => {
  const practitionersParam = Array.isArray(practitioners) ? practitioners.join(",") : "";
  return useQuery({
    queryKey: ["reports", "total-month", clinic, month, practitionersParam],
    queryFn: async () => {
      const params = new URLSearchParams({ month, practitioners: practitionersParam });
      const response = await api.get(
        `${endpoints.GET_REPORT_TOTAL_MONTH_PRACTITIONERS(clinic)}?${params}`,
      );
      return response.data;
    },
    enabled: !!clinic && !!month && !!practitionersParam,
    ...options,
  });
};

// ==================== PRACTITIONERS ====================

export const usePractitioners = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["practitioners", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_ALL_PRACTITIONERS, filters);
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

export const useUpdatePractitioner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch(endpoints.UPDATE_PRACTITIONER(id), data);
      return response.data;
    },
    onSuccess: async (data) => {
      // await invalidation so mutateAsync resolves only after the list has refetched
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["practitioners"] }),
        queryClient.invalidateQueries({ queryKey: ["practitioners", data.id] }),
        // Active-practitioner counts feed overview badges (x/y REPORTS SUBMITTED)
        queryClient.invalidateQueries({ queryKey: ["overview"] }),
      ]);
    },
  });
};

export const useUploadPractitioners = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post(endpoints.UPLOAD_PRACTITIONERS, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practitioners"] });
    },
  });
};

// ==================== PATIENTS ====================

export const useCreatePatient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patientData) => {
      const response = await api.post(endpoints.CREATE_PATIENT, patientData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
    },
  });
};

export const useUpdatePatient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch(endpoints.UPDATE_PATIENT(id), data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      queryClient.invalidateQueries({ queryKey: ["patients", data.id] });
    },
  });
};

export const useDeletePatient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(endpoints.DELETE_PATIENT(id));
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
    },
  });
};

export const usePatientsWithFilters = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["patients", "filtered", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_ALL_PATIENTS, filters);
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!filters.clinic && (!!filters.date || !!filters.search),
    ...options,
  });
};

// ==================== FORECASTS ====================

export const useForecasts = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["forecasts", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_ALL_FORECASTS, filters);
      const response = await api.get(url);
      return response.data;
    },
    // Support both single-date and date-range (month-level) fetching
    enabled: !!filters.clinic && (!!filters.date || !!filters.date_from),
    ...options,
  });
};

// Fetches forecast data for multiple clinics in a single request.
// Returns { clinicCode: [...items] } — one array per clinic, same shape as useForecasts.
// Used by the Admin Forecasting tab to replace N individual useForecasts calls with one batch call.
export const useForecastsBatch = (clinics = [], filters = {}, options = {}) => {
  const sorted = Array.from(new Set((clinics || []).filter(Boolean)))
    .map((c) => String(c).toUpperCase())
    .sort();
  const enabled = options.enabled !== false && sorted.length > 0 && !!filters.date_from;
  return useQuery({
    queryKey: ["forecasts", "batch", sorted, filters.date_from, filters.date_to],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_ALL_FORECASTS, {
        ...filters,
        clinics: sorted.join(","),
      });
      const response = await api.get(url);
      return response.data; // { clinicCode: [...items] }
    },
    enabled,
    ...options,
  });
};

export const useForecastMtd = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["forecasts", "mtd", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_FORECAST_MTD, filters);
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!filters.clinic && !!filters.date && options.enabled !== false,
    ...options,
  });
};

export const useCreateForecast = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (forecastData) => {
      const response = await api.post(endpoints.CREATE_FORECAST, forecastData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forecasts"] });
      // Forecasts tab (Admin) + home stat cards use overview aggregate endpoints
      queryClient.invalidateQueries({ queryKey: ["overview", "forecast-total"] });
      queryClient.invalidateQueries({ queryKey: ["overview", "clinic"] });
    },
  });
};

export const useUpdateForecast = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch(endpoints.UPDATE_FORECAST(id), data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["forecasts"] });
      queryClient.invalidateQueries({ queryKey: ["forecasts", data.id] });
      queryClient.invalidateQueries({ queryKey: ["overview", "forecast-total"] });
      queryClient.invalidateQueries({ queryKey: ["overview", "clinic"] });
    },
  });
};

// ==================== CLINICS ====================

export const useClinics = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["clinics", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_ALL_CLINICS, filters);
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

export const useClinicSubsidiaryMap = (options = {}) => {
  return useQuery({
    queryKey: ["clinics", "subsidiary-map"],
    queryFn: async () => {
      const response = await api.get(endpoints.GET_CLINIC_SUBSIDIARY_MAP);
      return response.data;
    },
    ...options,
  });
};

/**
 * One-call lookup pairing each clinic's id + name with its active NetSuite
 * bank account name. Response: { results: [{ id, clinic, clinic_name,
 * bank_account_id, bank_name }], count }.
 * Pass { include_inactive: true } to include sold/closed clinics.
 */
export const useClinicBankAccounts = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["clinics", "bank-accounts", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_CLINIC_BANK_ACCOUNTS, filters, {
        clean: true,
      });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

export const useUpdateClinic = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch(endpoints.UPDATE_CLINIC(id), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clinics"] });
    },
  });
};

// ==================== PRACTITIONER PROFILES ====================

export const useVerifyPractitionerName = () => {
  return useMutation({
    mutationFn: async (firstName) => {
      const response = await api.get(
        `${endpoints.VERIFY_PRACTITIONER_NAME}?first_name=${encodeURIComponent(firstName)}`,
      );
      return response.data;
    },
  });
};

/**
 * Search practitioners via API (across all accessible clinics).
 * Returns a mutation so the caller controls when to trigger the search.
 */
export const useSearchPractitioners = () => {
  return useMutation({
    mutationFn: async ({ search, status: statusFilter = "active" }) => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      const response = await api.get(`${endpoints.GET_ALL_PRACTITIONERS}?${params.toString()}`);
      return response.data;
    },
  });
};

// ==================== COLLECTION BREAKDOWN ====================

export const useCollectionBreakdowns = (clinic, filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["collection-breakdowns", "clinic", clinic, filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_CLINIC_COLLECTION_BREAKDOWNS(clinic), filters);
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!clinic, // Only fetch when clinic is provided
    ...options,
  });
};

// Fetch breakdowns for many clinics in ONE request.
// Returns { clinics: [...], results: { CLINIC_CODE: [rows...] } }.
export const useMultiClinicCollectionBreakdowns = (clinics = [], filters = {}, options = {}) => {
  const sorted = Array.from(new Set((clinics || []).filter(Boolean)))
    .map((c) => String(c).toUpperCase())
    .sort();
  const enabled = options.enabled !== false && sorted.length > 0;
  return useQuery({
    queryKey: ["collection-breakdowns", "multi-clinics", sorted, filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_MULTI_CLINIC_COLLECTION_BREAKDOWNS, {
        ...filters,
        clinics: sorted.join(","),
      });
      const response = await api.get(url);
      return response.data;
    },
    enabled,
    ...options,
  });
};

/**
 * `useMultiClinicCollectionBreakdowns` split into one request per month range
 * (`ranges`: [{ from, to }]), each keyed like the single-range hook so a month
 * already loaded is never fetched again. Returns `{ data: { results }, isLoading }`
 * with each clinic's rows from every month concatenated.
 */
export const useMultiClinicCollectionBreakdownsForMonths = (
  clinics = [],
  ranges = [],
  options = {},
) => {
  const sorted = Array.from(new Set((clinics || []).filter(Boolean)))
    .map((c) => String(c).toUpperCase())
    .sort();
  return useQueries({
    queries: ranges.map(({ from, to }) => {
      const filters = { date_from: from, date_to: to };
      return {
        queryKey: ["collection-breakdowns", "multi-clinics", sorted, filters],
        queryFn: async () => {
          const url = buildUrlWithParams(endpoints.GET_MULTI_CLINIC_COLLECTION_BREAKDOWNS, {
            ...filters,
            clinics: sorted.join(","),
          });
          const response = await api.get(url);
          return response.data;
        },
        enabled: options.enabled !== false && sorted.length > 0,
      };
    }),
    combine: combineResultsByClinic,
  });
};

// Merges `{ results: { CLINIC: rows[] | { date: totals } } }` across months.
function combineResultsByClinic(results) {
  const isLoading = results.some((r) => r.isLoading);
  if (results.every((r) => !r.data)) return { data: undefined, isLoading };
  const merged = {};
  results.forEach((r) => {
    Object.entries(r.data?.results || {}).forEach(([clinic, value]) => {
      if (Array.isArray(value)) merged[clinic] = [...(merged[clinic] || []), ...value];
      else merged[clinic] = { ...(merged[clinic] || {}), ...(value || {}) };
    });
  });
  return { data: { results: merged }, isLoading };
}

export const useCreateCollectionBreakdown = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clinic, data }) => {
      const response = await api.post(endpoints.CREATE_COLLECTION_BREAKDOWN(clinic), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collection-breakdowns"] });
    },
  });
};

export const useUpdateCollectionBreakdown = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clinic, id, data }) => {
      const response = await api.patch(endpoints.UPDATE_COLLECTION_BREAKDOWN(clinic, id), data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["collection-breakdowns"] });
      queryClient.invalidateQueries({ queryKey: ["collection-breakdowns", data.id] });
    },
  });
};

// ==================== OVERVIEW ====================

export const useOverviewClinicSummary = (filters = {}, options = {}) => {
  const { enablePolling = false, pollIntervalMs = 60 * 1000, ...queryOptions } = options;

  return useQuery({
    queryKey: ["overview", "clinic", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_OVERVIEW_CLINIC_SUMMARY, filters);
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!filters.date || (!!filters.date_from && !!filters.date_to), // Support both single date and date range
    // `enablePolling` used to be spread straight into useQuery, where React
    // Query ignores unknown keys — so it did nothing. With the global defaults
    // (staleTime 5min, no refetch on focus) the reporting status table could show
    // five-minute-old submissions and only a page reload fixed it. Opt this query
    // out of those defaults when a caller asks for live data.
    ...(enablePolling
      ? {
          staleTime: 0,
          refetchOnMount: "always",
          refetchOnWindowFocus: true,
          refetchInterval: pollIntervalMs,
          // Don't keep polling a tab nobody is looking at.
          refetchIntervalInBackground: false,
        }
      : {}),
    ...queryOptions,
  });
};

export const useOverviewPerformance = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["overview", "performance", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_OVERVIEW_PERFORMANCE, filters);
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!filters.date || (!!filters.date_from && !!filters.date_to), // Support both single date and date range
    ...options,
  });
};

export const useOverviewPatients = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["overview", "patients", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_OVERVIEW_PATIENTS, filters);
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!filters.date || (!!filters.date_from && !!filters.date_to), // Support both single date and date range
    ...options,
  });
};

export const useOverviewForecastTotal = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["overview", "forecast-total", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_OVERVIEW_FORECAST_TOTAL, filters);
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!filters.date || (!!filters.date_from && !!filters.date_to), // Support both single date and date range
    ...options,
  });
};

// ==================== ADMIN: REPORTS ====================

export const useReports = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["reports", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(adminEndpoints.GET_ALL_REPORTS, filters);
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

export const useReport = (id, options = {}) => {
  return useQuery({
    queryKey: ["reports", id],
    queryFn: async () => {
      const response = await api.get(adminEndpoints.GET_REPORT(id));
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};

export const useDeleteReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(adminEndpoints.DELETE_REPORT(id));
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
  });
};

export const useAdminReportClinicData = (clinic, filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["reports", "clinic-data", clinic, filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_CLINIC_DATA(clinic), filters);
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!clinic,
    ...options,
  });
};

export const useAdminReportTotal = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["reports", "total", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_REPORT_TOTAL, filters);
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!filters.clinic && !!filters.date,
    ...options,
  });
};

// ==================== ADMIN: PRACTITIONERS BY CLINIC ====================

export const usePractitionersByClinic = (clinicCode, filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["practitioners", clinicCode, filters],
    queryFn: async () => {
      const url = buildUrlWithParams(
        adminEndpoints.GET_ALL_PRACTITIONERS_BY_CLINIC(clinicCode),
        filters,
      );
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!clinicCode,
    ...options,
  });
};

export const useCreatePractitionerByClinic = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clinicCode, data }) => {
      const response = await api.post(adminEndpoints.CREATE_PRACTITIONER, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["practitioners"] });
      queryClient.invalidateQueries({
        queryKey: ["practitioners", variables.clinicCode],
      });
    },
  });
};

export const useUpdatePractitionerByClinic = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clinicCode, id, data, method = "patch" }) => {
      const apiMethod = method === "put" ? api.put : api.patch;
      const response = await apiMethod(
        adminEndpoints.UPDATE_PRACTITIONER_BY_CLINIC(clinicCode, id),
        data,
      );
      return response.data;
    },
    onSuccess: async (data, variables) => {
      // await invalidation so callers awaiting mutateAsync see fresh data immediately
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["practitioners"] }),
        queryClient.invalidateQueries({
          queryKey: ["practitioners", variables.clinicCode, variables.id],
        }),
        // Active-practitioner counts feed overview badges (x/y REPORTS SUBMITTED)
        queryClient.invalidateQueries({ queryKey: ["overview"] }),
      ]);
    },
  });
};

export const useDeletePractitionerByClinic = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clinicCode, id }) => {
      await api.delete(adminEndpoints.DELETE_PRACTITIONER_BY_CLINIC(clinicCode, id));
      return { clinicCode, id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practitioners"] });
    },
  });
};

// ==================== ADMIN: MONTHLY COMPENSATION ====================

export const useMonthlyCompensations = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["monthly-compensation", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(adminEndpoints.GET_ALL_MONTHLY_COMPENSATIONS, filters, {
        clean: true,
      });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

export const useAssociateFeeRows = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["monthly-compensation", "associate-fee", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_ASSOCIATE_FEE_ROWS, filters, {
        clean: true,
      });
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!filters.month,
    ...options,
  });
};

/**
 * Fan-out variant of useAssociateFeeRows that fetches one request per month
 * and merges the resulting rows. Each row already carries its `month` field
 * from the backend so consumers can group/sort/filter as needed.
 *
 * filters.months: string[] (required, non-empty)
 * filters.clinic: optional comma-separated clinic codes (backend handles split)
 * Other filters are forwarded to every per-month request.
 */
export const useAssociateFeeRowsMulti = (filters = {}, options = {}) => {
  const { months = [], ...rest } = filters;
  const stableMonths = Array.isArray(months) ? [...months].sort() : [];
  return useQuery({
    queryKey: ["monthly-compensation", "associate-fee", "multi", stableMonths, rest],
    queryFn: async () => {
      if (stableMonths.length === 0) return { results: [], count: 0 };
      const responses = await Promise.all(
        stableMonths.map((m) => {
          const url = buildUrlWithParams(
            endpoints.GET_ASSOCIATE_FEE_ROWS,
            { ...rest, month: m },
            { clean: true },
          );
          return api.get(url).then((r) => r.data?.results ?? r.data ?? []);
        }),
      );
      const results = responses.flat();
      return { results, count: results.length };
    },
    enabled: stableMonths.length > 0 && (options.enabled ?? true),
    ...options,
  });
};

export const useMonthlyCompensation = (id, options = {}) => {
  return useQuery({
    queryKey: ["monthly-compensation", id],
    queryFn: async () => {
      const response = await api.get(adminEndpoints.GET_MONTHLY_COMPENSATION(id));
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};

export const useCreateMonthlyCompensation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(adminEndpoints.CREATE_MONTHLY_COMPENSATION, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["monthly-compensation"] });
    },
  });
};

export const useUpdateMonthlyCompensation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch(adminEndpoints.UPDATE_MONTHLY_COMPENSATION(id), data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["monthly-compensation"] });
      queryClient.invalidateQueries({
        queryKey: ["monthly-compensation", data.id],
      });
    },
  });
};

export const useSendPdfEmail = () => {
  return useMutation({
    mutationFn: async (id) => {
      const response = await api.post(adminEndpoints.SEND_PDF_EMAIL(id));
      return response.data;
    },
  });
};

export const useBulkSendPdfEmail = () => {
  return useMutation({
    mutationFn: async (ids) => {
      const response = await api.post(adminEndpoints.BULK_SEND_PDF_EMAIL, { ids });
      return response.data;
    },
  });
};

export const useBulkEmailStatus = (jobId, options = {}) => {
  return useQuery({
    queryKey: ["bulk-email-status", jobId],
    queryFn: async () => {
      const response = await api.get(adminEndpoints.BULK_EMAIL_STATUS(jobId));
      return response.data;
    },
    enabled: !!jobId,
    ...options,
  });
};

export const useDownloadPdf = () => {
  return useMutation({
    mutationFn: async ({ id, practitionerName, month }) => {
      const response = await api.get(adminEndpoints.DOWNLOAD_PDF(id), {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(practitionerName || "statement").replace(/ /g, "_")}_${month || "report"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useDownloadLabInvoiceZip = () => {
  return useMutation({
    mutationFn: async ({ clinic, practitionerId, practitionerName, month }) => {
      const [year, monthNumber] = String(month || "")
        .split("-")
        .map(Number);
      const lastDay = new Date(year, monthNumber, 0).getDate();
      const dateFrom = `${month}-01`;
      const dateTo = `${month}-${String(lastDay).padStart(2, "0")}`;
      const response = await api.get(endpoints.LAB_INVOICE_DOWNLOAD_ZIP, {
        params: {
          clinic,
          practitioner: practitionerId,
          date_from: dateFrom,
          date_to: dateTo,
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      const safeName = `${clinic}_${practitionerName || practitionerId}_${month}_lab_invoices`
        .replace(/[^A-Za-z0-9._-]+/g, "_")
        .replace(/^_+|_+$/g, "");
      link.href = url;
      link.download = `${safeName || "lab_invoices"}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  });
};

export const usePreviewPdf = () => {
  return useMutation({
    mutationFn: async ({ id }) => {
      const response = await api.get(adminEndpoints.DOWNLOAD_PDF(id), {
        responseType: "blob",
      });
      return window.URL.createObjectURL(response.data);
    },
  });
};

// ==================== CLINIC: MONTHLY COMPENSATION ====================

export const useMonthlyCompensationsByClinic = (clinicCode, filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["monthly-compensation", "clinic", clinicCode, filters],
    queryFn: async () => {
      const url = buildUrlWithParams(
        clinicEndpoints.GET_MONTHLY_COMPENSATION_BY_CLINIC(clinicCode),
        filters,
      );
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!clinicCode,
    ...options,
  });
};

export const useCreateMonthlyCompensationForClinic = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clinicCode, data }) => {
      const response = await api.post(
        clinicEndpoints.CREATE_MONTHLY_COMPENSATION_FOR_CLINIC(clinicCode),
        data,
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["monthly-compensation"] });
      queryClient.invalidateQueries({
        queryKey: ["monthly-compensation", "clinic", variables.clinicCode],
      });
    },
  });
};

export const useUpdateMonthlyCompensationForClinic = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clinicCode, id, data }) => {
      const response = await api.patch(
        clinicEndpoints.UPDATE_MONTHLY_COMPENSATION_FOR_CLINIC(clinicCode, id),
        data,
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["monthly-compensation"] });
      queryClient.invalidateQueries({
        queryKey: ["monthly-compensation", "clinic", variables.clinicCode],
      });
      queryClient.invalidateQueries({
        queryKey: ["monthly-compensation", data.id],
      });
    },
  });
};

export const useBulkApproveMonthlyCompensation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, approved }) => {
      const response = await api.post(adminEndpoints.BULK_APPROVE, { ids, approved });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["monthly-compensation"] });
    },
  });
};

// ==================== CLINIC: REPORTS (READ-ONLY) ====================

export const useReportsClinics = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["reports", "clinics", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(clinicEndpoints.GET_REPORTS_CLINICS, filters);
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

export const useClinicData = (clinicName, filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["reports", "clinic-data", clinicName, filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_CLINIC_DATA(clinicName), filters);
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!clinicName,
    ...options,
  });
};

export const useClinicMonthlyTotals = (clinic, date, options = {}) => {
  return useQuery({
    queryKey: ["reports", "total", clinic, date],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_REPORT_TOTAL, { clinic, date });
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!clinic && !!date,
    ...options,
  });
};

export const useReportsSummary = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["reports", "summary", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_REPORTS_SUMMARY, filters);
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

// Actual DDS/HYG collection totals per clinic per day, for multiple clinics in one request.
// Returns { clinicCode: [{ date, dds, hyg }, ...] }. Used by the Cash Flow page to fill the
// DDS/HYG inflow rows for past dates (forecasts fill today/future — see useForecastsBatch).
export const useReportsCollectionSummary = (clinics = [], filters = {}, options = {}) => {
  const sorted = Array.from(new Set((clinics || []).filter(Boolean)))
    .map((c) => String(c).toUpperCase())
    .sort();
  const enabled =
    options.enabled !== false && sorted.length > 0 && !!filters.date_from && !!filters.date_to;
  return useQuery({
    queryKey: ["reports", "collection-summary", sorted, filters.date_from, filters.date_to],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_REPORTS_COLLECTION_SUMMARY, {
        ...filters,
        clinics: sorted.join(","),
      });
      const response = await api.get(url);
      return response.data;
    },
    enabled,
    ...options,
  });
};

// ==================== CLINIC: CLINIC DETAIL ====================

export const useClinicDetail = (clinicCode, options = {}) => {
  const { includeInactive = false, ...queryOptions } = options;
  return useQuery({
    queryKey: ["clinics", "detail", clinicCode, { includeInactive }],
    queryFn: async () => {
      const url = buildUrlWithParams(clinicEndpoints.GET_CLINIC_DETAIL(clinicCode), {
        include_inactive: includeInactive || undefined,
      });
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!clinicCode,
    ...queryOptions,
  });
};

// ==================== PRACTITIONER VACATIONS ====================

export const usePractitionerVacations = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["practitioner-vacations", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_PRACTITIONER_VACATIONS, filters);
      const response = await api.get(url);
      return response.data;
    },
    // practitioner_id (Practitioner PK) replaces profile_id — vacations are now per clinic-assignment.
    enabled: !!filters.practitioner_id || !!filters.clinic,
    ...pollingOptions(options),
  });
};

export const useCreatePractitionerVacation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(endpoints.CREATE_PRACTITIONER_VACATION, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practitioner-vacations"] });
    },
  });
};

export const useUpdatePractitionerVacation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch(endpoints.UPDATE_PRACTITIONER_VACATION(id), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practitioner-vacations"] });
    },
  });
};

export const useDeletePractitionerVacation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(endpoints.DELETE_PRACTITIONER_VACATION(id));
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practitioner-vacations"] });
    },
  });
};

// ==================== CLINIC VACATIONS ====================

export const useClinicVacations = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["clinic-vacations", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_CLINIC_VACATIONS, filters);
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!filters.clinic,
    ...pollingOptions(options),
  });
};

export const useCreateClinicVacation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(endpoints.CREATE_CLINIC_VACATION, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clinic-vacations"] });
    },
  });
};

export const useUpdateClinicVacation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch(endpoints.UPDATE_CLINIC_VACATION(id), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clinic-vacations"] });
    },
  });
};

export const useDeleteClinicVacation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(endpoints.DELETE_CLINIC_VACATION(id));
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clinic-vacations"] });
    },
  });
};

// ==================== PRACTITIONER WEEKLY SCHEDULES ====================
// practitioner_weekly_schedule — per-clinic recurring hours. One base row per
// practitioner plus any number of date-bounded rows that override it inside their range.
// Resolve precedence with the helpers in @utils/weeklySchedule.

export const usePractitionerWeeklySchedules = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["practitioner-weekly-schedules", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_PRACTITIONER_WEEKLY_SCHEDULES, filters);
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!filters.practitioner_id || !!filters.clinic,
    ...pollingOptions(options),
  });
};

export const useCreatePractitionerWeeklySchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(endpoints.CREATE_PRACTITIONER_WEEKLY_SCHEDULE, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practitioner-weekly-schedules"] });
    },
  });
};

export const useUpdatePractitionerWeeklySchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch(endpoints.UPDATE_PRACTITIONER_WEEKLY_SCHEDULE(id), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practitioner-weekly-schedules"] });
    },
  });
};

export const useDeletePractitionerWeeklySchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(endpoints.DELETE_PRACTITIONER_WEEKLY_SCHEDULE(id));
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practitioner-weekly-schedules"] });
    },
  });
};

/**
 * Upsert one weekly-schedule row — the mutation the schedule editors actually want.
 *
 * mutateAsync({ practitionerId, schedule, effectiveStart, effectiveEnd, existingRows })
 *   - effectiveStart/effectiveEnd omitted  → the practitioner's base (recurring) schedule
 *   - both supplied                        → a dated row overriding the base in that range
 *
 * PATCHes the matching row when one already exists (same practitioner + same range) and
 * POSTs otherwise, so callers don't have to track row ids. existingRows is whatever
 * usePractitionerWeeklySchedules already returned for that practitioner.
 */
export const useSavePractitionerWeeklySchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      practitionerId,
      schedule,
      effectiveStart = null,
      effectiveEnd = null,
      existingRows = [],
    }) => {
      const start = effectiveStart || null;
      const end = effectiveEnd || null;
      const match = existingRows.find(
        (row) => (row.effective_start ?? null) === start && (row.effective_end ?? null) === end,
      );

      const payload = {
        practitioner: practitionerId,
        schedule,
        effective_start: start,
        effective_end: end,
      };

      const response = match
        ? await api.patch(endpoints.UPDATE_PRACTITIONER_WEEKLY_SCHEDULE(match.id), payload)
        : await api.post(endpoints.CREATE_PRACTITIONER_WEEKLY_SCHEDULE, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practitioner-weekly-schedules"] });
      // The practitioner payload carries weekly_schedule (its base row), so keep the
      // roster caches in step with the row we just wrote.
      queryClient.invalidateQueries({ queryKey: ["practitioners"] });
    },
  });
};

// ==================== PRACTITIONER SCHEDULE OVERRIDES ====================

export const usePractitionerScheduleOverrides = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["practitioner-schedule-overrides", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_PRACTITIONER_SCHEDULE_OVERRIDES, filters);
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!filters.practitioner_id || !!filters.clinic,
    ...pollingOptions(options),
  });
};

export const useCreatePractitionerScheduleOverride = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(endpoints.CREATE_PRACTITIONER_SCHEDULE_OVERRIDE, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practitioner-schedule-overrides"] });
    },
  });
};

export const useUpdatePractitionerScheduleOverride = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch(endpoints.UPDATE_PRACTITIONER_SCHEDULE_OVERRIDE(id), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practitioner-schedule-overrides"] });
    },
  });
};

export const useDeletePractitionerScheduleOverride = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(endpoints.DELETE_PRACTITIONER_SCHEDULE_OVERRIDE(id));
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practitioner-schedule-overrides"] });
    },
  });
};

// ==================== CLINIC SCHEDULE OVERRIDES ====================

export const useClinicScheduleOverrides = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["clinic-schedule-overrides", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_CLINIC_SCHEDULE_OVERRIDES, filters);
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!filters.clinic,
    ...pollingOptions(options),
  });
};

export const useCreateClinicScheduleOverride = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(endpoints.CREATE_CLINIC_SCHEDULE_OVERRIDE, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clinic-schedule-overrides"] });
    },
  });
};

export const useUpdateClinicScheduleOverride = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch(endpoints.UPDATE_CLINIC_SCHEDULE_OVERRIDE(id), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clinic-schedule-overrides"] });
    },
  });
};

export const useDeleteClinicScheduleOverride = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(endpoints.DELETE_CLINIC_SCHEDULE_OVERRIDE(id));
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clinic-schedule-overrides"] });
    },
  });
};

// ==================== COPY SCHEDULE ====================

export const useCopySchedule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(endpoints.COPY_SCHEDULE, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["practitioner-schedule-overrides"] });
    },
  });
};

// ==================== AUDIT LOGS ====================

export const useAuditLogs = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["audit-logs", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_AUDIT_LOGS, filters);
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

/**
 * Fetch practitioner production totals by subsidiary IDs and date range.
 * Params: { subsidiaryIds: number[], startDate: "YYYY-MM-DD", endDate: "YYYY-MM-DD" }
 */
export const usePractitionerProduction = (
  { subsidiaryIds, startDate, endDate } = {},
  options = {},
) => {
  const idsCsv =
    Array.isArray(subsidiaryIds) && subsidiaryIds.length > 0 ? subsidiaryIds.join(",") : "";
  const enabled = !!idsCsv && !!startDate && !!endDate;

  return useQuery({
    queryKey: ["reports", "practitioner-production", idsCsv, startDate, endDate],
    queryFn: async () => {
      const url = buildUrlWithParams(
        endpoints.GET_PRACTITIONER_PRODUCTION,
        { subsidiary_id: idsCsv, startDate, endDate },
        { clean: true },
      );
      const response = await api.get(url);
      return response.data;
    },
    enabled,
    ...options,
  });
};

// ==================== NETSUITE ====================

export const useCreateNetsuiteJournalEntry = () => {
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(endpoints.CREATE_NETSUITE_JOURNAL_ENTRY, data);
      return response.data;
    },
    // Intentionally NO invalidateQueries here. The NetSuite journal-entry
    // search is slow (SuiteQL, ~1–2s), so we don't refetch after a create.
    // The caller tracks the just-created clinic in local state and renders a
    // "Done!!" indicator; the next natural refetch (month change, remount)
    // will reconcile with NetSuite.
  });
};

export const useCreateNetsuiteIntercompanyJournalEntry = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(
        endpoints.CREATE_NETSUITE_ADVANCED_INTERCOMPANY_JOURNAL_ENTRY,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["netsuite", "advanced-intercompany-journal-entries", "search"],
      });
      queryClient.invalidateQueries({
        queryKey: ["netsuite", "cash-management-journal-entries", "search"],
      });
    },
  });
};

/**
 * "Sync" — force the next NetSuite read to be live.
 *
 * NetSuite searches are served from a shared server-side SuiteQL cache (up to
 * an hour old), so a journal entry or check posted in NetSuite directly, or by
 * someone else's session, can be missing from the Journal Entry / Check status
 * columns. This drops that cache, then invalidates every client query whose
 * data comes from NetSuite plus the monthly-compensation rows the two tabs are
 * built on, so the page refetches from scratch.
 *
 * Optional `source` (string) is logged server-side to say which button fired.
 */
export const useInvalidateNetsuiteCache = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ source } = {}) => {
      const response = await api.post(endpoints.INVALIDATE_NETSUITE_CACHE, { source });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["netsuite"] });
      queryClient.invalidateQueries({ queryKey: ["monthly-compensation"] });
    },
  });
};

export const useCreateNetsuiteCheck = () => {
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(endpoints.CREATE_NETSUITE_CHECK, data);
      return response.data;
    },
    // Intentionally NO invalidateQueries here — see note above.
  });
};

/**
 * Bulk-create NetSuite Checks. Each step is processed independently on the
 * backend, so a failure in one step does NOT abort the others.
 *
 * Input:  { steps: [{ body: <single-check payload> }, ...] }
 * Output: {
 *   summary: { total, succeeded, failed },
 *   results: [{ index, ok, status, data?, error?, error_code? }, ...]
 * }
 */
export const useBulkCreateNetsuiteCheck = () => {
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(endpoints.BULK_CREATE_NETSUITE_CHECK, data);
      return response.data;
    },
    // No invalidate — caller tracks per-row "just created" state locally.
  });
};

/**
 * Search NetSuite checks by entity + memo (case-insensitive LIKE).
 * Params: { entity, entities, memo, month }
 *   - entity: single vendor/entity id
 *   - entities: array of ids (batched into one request as CSV)
 *   - memo: base memo substring (e.g. "Associate Fee")
 *   - month: optional "YYYY-MM"; backend expands to match any of
 *     "March 2026", "Mar 2026", "03-2026", "3/2026", "2026-03", etc.
 *   - dateFrom: optional "YYYY-MM-DD" inclusive lower bound on trandate, for
 *     callers spanning several months. Pass one whenever `month` is omitted:
 *     the memo match can't use an index, so an unbounded search scans every
 *     check those entities ever received. Ignored when `month` is set.
 *   - amounts: optional array of numbers; backend filters
 *     `grosstotal IN (...)` so only checks whose total matches one of
 *     the expected amounts come back.
 */
export const useSearchNetsuiteChecks = (
  { entity, entities, memo, month, dateFrom } = {},
  options = {},
) => {
  const list = Array.isArray(entities)
    ? entities.filter((v) => v !== null && v !== undefined && v !== "")
    : [];
  const entitiesCsv = list.length > 0 ? list.join(",") : "";
  const hasAny = !!entity || entitiesCsv.length > 0;

  return useQuery({
    queryKey: ["netsuite", "checks", "search", entity, entitiesCsv, memo, month, dateFrom],
    queryFn: async () => {
      const params = { memo };
      if (entitiesCsv) params.entities = entitiesCsv;
      if (entity) params.entity = entity;
      if (month) params.month = month;
      if (dateFrom) params.date_from = dateFrom;
      const url = buildUrlWithParams(endpoints.SEARCH_NETSUITE_CHECKS, params);
      const response = await api.get(url);
      return response.data;
    },
    enabled: hasAny && !!memo,
    ...options,
  });
};

/**
 * Search NetSuite checks by SUBSIDIARY (the clinic that cut the check) —
 * no vendor/entity filter and no memo filter, so this returns every
 * non-voided check the subsidiary issued in the requested date range.
 *
 * Params: { subsidiary, subsidiaries, dateFrom, dateTo, date, dates, cleared }
 *   - subsidiary:   single netsuite_subsidiary_id
 *   - subsidiaries: array of ids (sent as one CSV request)
 *   - pairs:        array of "<subsidiaryId>:<accountId>" strings. Each
 *                   subsidiary is matched against ITS OWN accounts only, so
 *                   several clinics in one request can't cross-match (clinic
 *                   A's check paid from clinic B's account). Use this whenever
 *                   more than one subsidiary is in play.
 *   - account/accounts: flat NetSuite bank account id(s), applied to every
 *                   subsidiary in the request — only safe for a single
 *                   subsidiary. Omit both for every account.
 *   - dateFrom/dateTo: inclusive "YYYY-MM-DD" trandate range; either side may
 *                   be omitted for an open-ended filter
 *   - date/dates:   specific "YYYY-MM-DD" days (array sent as one CSV request);
 *                   combines with the range as an OR
 *   - dateField:    which column the dates filter — "trandate" (default, when
 *                   the check was written) or "cleareddate" (when it left the
 *                   bank). Ask for cleared checks in a window by cleareddate:
 *                   a February check can clear in March.
 *   - cleared:      "T" (cleared only) | "F" (uncleared only) | undefined (both)
 *
 * Response shape:
 * {
 *   subsidiaries: [12, 13],
 *   dates: ["2026-03-14"],
 *   date_from: "2026-02-01" | null,
 *   date_to: "2026-03-31" | null,
 *   cleared: "F" | "T" | null,
 *   count: 2,
 *   items: [{ id, trandate, transactionnumber, entity_id, memo, subsidiary_id,
 *             subsidiary, grosstotal, amount, cleared, cleareddate, link }]
 * }
 */
export const useSearchNetsuiteChecksBySubsidiary = (
  {
    subsidiary,
    subsidiaries,
    account,
    accounts,
    pairs,
    dateFrom,
    dateTo,
    date,
    dates,
    dateField,
    cleared,
    hasTranid,
  } = {},
  options = {},
) => {
  const toCsv = (single, list) => {
    const values = Array.isArray(list) ? list : [];
    const all = [...values, ...(single !== null && single !== undefined ? [single] : [])]
      .filter((v) => v !== null && v !== undefined && v !== "")
      .map((v) => String(v));
    return Array.from(new Set(all)).join(",");
  };

  const subsidiariesCsv = toCsv(subsidiary, subsidiaries);
  const accountsCsv = toCsv(account, accounts);
  const pairsCsv = toCsv(null, pairs);
  const datesCsv = toCsv(date, dates);
  const from = dateFrom ? String(dateFrom) : "";
  const to = dateTo ? String(dateTo) : "";
  const clearedParam =
    cleared === null || cleared === undefined || cleared === ""
      ? ""
      : String(cleared).toUpperCase();

  return useQuery({
    queryKey: [
      "netsuite",
      "checks",
      "search-by-subsidiary",
      subsidiariesCsv,
      accountsCsv,
      pairsCsv,
      datesCsv,
      String(hasTranid ?? ""),
      from,
      to,
      dateField ?? "",
      clearedParam,
    ],
    queryFn: async () => {
      // `pairs` carries its own subsidiary ids, so either one alone is a valid
      // request.
      const params = {};
      if (subsidiariesCsv) params.subsidiaries = subsidiariesCsv;
      if (accountsCsv) params.accounts = accountsCsv;
      if (pairsCsv) params.pairs = pairsCsv;
      if (datesCsv) params.dates = datesCsv;
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      if (dateField) params.date_field = dateField;
      if (clearedParam) params.cleared = clearedParam;
      // Numbered cheques only. Sent as a filter rather than dropped after the
      // fact: records without a check number are most of the table.
      if (hasTranid !== undefined && hasTranid !== null && hasTranid !== "") {
        params.has_tranid = String(hasTranid);
      }
      const url = buildUrlWithParams(endpoints.SEARCH_NETSUITE_CHECKS_BY_SUBSIDIARY, params);
      const response = await api.get(url);
      return response.data;
    },
    enabled: subsidiariesCsv.length > 0 || pairsCsv.length > 0,
    ...options,
  });
};

/**
 * Fetch latest cleared check dates by NetSuite vendor id(s).
 * Params: { netsuiteVendorId, netsuiteVendorIds }
 *  - netsuiteVendorId: single vendor/entity id
 *  - netsuiteVendorIds: array of vendor/entity ids
 *
 * Response shape:
 * {
 *   vendor_ids: [604332, 604333],
 *   count: 7,
 *   items: {
 *     "604332": { netsuite_vendor_id: 604332, count: 5, items: [...] },
 *     "604333": { netsuite_vendor_id: 604333, count: 2, items: [...] }
 *   }
 * }
 */
export const useSearchNetsuiteCheckClearedDates = (
  { netsuiteVendorId, netsuiteVendorIds } = {},
  options = {},
) => {
  const list = Array.isArray(netsuiteVendorIds)
    ? netsuiteVendorIds.filter((v) => v !== null && v !== undefined && v !== "")
    : [];
  const normalized = Array.from(new Set(list.map((v) => String(v)))).sort();
  const idsCsv = normalized.length > 0 ? normalized.join(",") : "";
  const hasAny = !!netsuiteVendorId || idsCsv.length > 0;

  return useQuery({
    queryKey: ["netsuite", "checks", "cleared-dates", netsuiteVendorId, idsCsv],
    queryFn: async () => {
      const params = {};
      if (idsCsv) params.netsuite_vendor_ids = idsCsv;
      if (netsuiteVendorId) params.netsuite_vendor_id = netsuiteVendorId;
      const url = buildUrlWithParams(endpoints.SEARCH_NETSUITE_CHECK_CLEARED_DATES, params);
      const response = await api.get(url);
      return response.data;
    },
    enabled: hasAny,
    ...options,
  });
};

/**
 * Search NetSuite journal entries by subsidiary + memo (case-insensitive LIKE).
 * Params: { subsidiary, subsidiaries, memo, month }
 *   - subsidiary: single id (back-compat)
 *   - subsidiaries: array of ids (batched into one request as CSV)
 *   - memo: base memo substring (e.g. "Production, Collections and Associate Fee")
 *   - month: optional "YYYY-MM"; backend expands to match any of
 *     "March 2026", "Mar 2026", "03-2026", "3/2026", "2026-03", etc.
 */
export const useSearchNetsuiteJournalEntries = (
  { subsidiary, subsidiaries, memo, month } = {},
  options = {},
) => {
  const list = Array.isArray(subsidiaries)
    ? subsidiaries.filter((v) => v !== null && v !== undefined && v !== "")
    : [];
  const subsidiariesCsv = list.length > 0 ? list.join(",") : "";
  const hasAny = !!subsidiary || subsidiariesCsv.length > 0;

  return useQuery({
    queryKey: ["netsuite", "journal-entries", "search", subsidiary, subsidiariesCsv, memo, month],
    queryFn: async () => {
      const params = { memo };
      if (subsidiariesCsv) params.subsidiaries = subsidiariesCsv;
      if (subsidiary) params.subsidiary = subsidiary;
      if (month) params.month = month;
      const url = buildUrlWithParams(endpoints.SEARCH_NETSUITE_JOURNAL_ENTRIES, params);
      const response = await api.get(url);
      return response.data;
    },
    enabled: hasAny && !!memo,
    ...options,
  });
};

/**
 * Search NetSuite journal entries by month-year using an inclusive trandate range.
 * Params: { month, memo }
 *   - month: required "YYYY-MM"
 *   - memo: optional substring (defaults to "Practice Payroll ending")
 */
export const useSearchNetsuiteJournalEntriesByMonthYear = (
  { month, memo = "Practice Payroll ending" } = {},
  options = {},
) => {
  return useQuery({
    queryKey: ["netsuite", "journal-entries", "search-by-month-year", month, memo],
    queryFn: async () => {
      const params = {};
      if (month) params.month = month;
      if (memo) params.memo = memo;
      const url = buildUrlWithParams(
        endpoints.SEARCH_NETSUITE_JOURNAL_ENTRIES_BY_MONTH_YEAR,
        params,
      );
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!month,
    ...options,
  });
};

/**
 * Search NetSuite advanced intercompany journal entries by memo (and optionally
 * subsidiary). Subsidiary is optional — the intercompany JE we create uses
 * SFC as header subsidiary, so a memo+month match is enough.
 *
 * Params: { memo, month, subsidiary, subsidiaries }
 */
export const useSearchNetsuiteAdvIntercompanyJournalEntries = (
  { memo, month, subsidiary, subsidiaries } = {},
  options = {},
) => {
  const list = Array.isArray(subsidiaries)
    ? subsidiaries.filter((v) => v !== null && v !== undefined && v !== "")
    : [];
  const subsidiariesCsv = list.length > 0 ? list.join(",") : "";

  return useQuery({
    queryKey: [
      "netsuite",
      "adv-intercompany-journal-entries",
      "search",
      subsidiary,
      subsidiariesCsv,
      memo,
      month,
    ],
    queryFn: async () => {
      const params = { memo };
      if (subsidiariesCsv) params.subsidiaries = subsidiariesCsv;
      if (subsidiary) params.subsidiary = subsidiary;
      if (month) params.month = month;
      const url = buildUrlWithParams(
        endpoints.SEARCH_NETSUITE_ADVANCED_INTERCOMPANY_JOURNAL_ENTRIES,
        params,
      );
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!memo,
    ...options,
  });
};

/**
 * Search NetSuite transactionLine rows for "Deposit to Safe" cash-management
 * journal entries. Backed by GET /api/netsuite/cash-management-journal-entries/search/.
 *
 * Params: { memo, subsidiary, subsidiaries, linecreateddate, dates, limit, offset }
 *   - memo: required substring (case-insensitive LIKE)
 *   - subsidiaries: array of ids OR subsidiary: single id (one required)
 *   - dates: array of "YYYY-MM-DD" OR linecreateddate: single date (one required)
 *   - limit/offset: optional NetSuite-side paging. Omit `limit` and every
 *     matching row comes back — which is what a caller that groups the whole
 *     result set by clinic needs. `count` is the unpaged total either way.
 *
 * Returns one row per (subsidiary, transaction) — frontend uses the
 * (subsidiary_id, linecreateddate) pair to map results back to clinics.
 */
export const useSearchNetsuiteCashManagementJournalEntries = (
  { memo, subsidiary, subsidiaries, linecreateddate, dates, limit, offset } = {},
  options = {},
) => {
  const subList = Array.isArray(subsidiaries)
    ? subsidiaries.filter((v) => v !== null && v !== undefined && v !== "")
    : [];
  const subsidiariesCsv = subList.length > 0 ? subList.join(",") : "";

  const dateList = Array.isArray(dates)
    ? dates.filter((v) => v !== null && v !== undefined && v !== "")
    : [];
  const datesCsv = dateList.length > 0 ? dateList.join(",") : "";

  return useQuery({
    queryKey: [
      "netsuite",
      "cash-management-journal-entries",
      "search",
      memo,
      subsidiary,
      subsidiariesCsv,
      linecreateddate,
      datesCsv,
      limit ?? null,
      offset ?? null,
    ],
    queryFn: async () => {
      const params = { memo };
      if (subsidiariesCsv) params.subsidiaries = subsidiariesCsv;
      if (subsidiary) params.subsidiary = subsidiary;
      if (datesCsv) params.dates = datesCsv;
      if (linecreateddate) params.linecreateddate = linecreateddate;
      if (limit) {
        params.limit = limit;
        params.offset = offset || 0;
      }
      const url = buildUrlWithParams(
        endpoints.SEARCH_NETSUITE_CASH_MANAGEMENT_JOURNAL_ENTRIES,
        params,
      );
      const response = await api.get(url);
      return response.data;
    },
    enabled:
      !!memo &&
      (!!subsidiary || subsidiariesCsv.length > 0) &&
      (!!linecreateddate || datesCsv.length > 0),
    ...options,
  });
};

/**
 * Search NORMAL NetSuite journal entries (`JournalEntry`) by memo, for Cash
 * Management's listing. Backed by
 * GET /api/netsuite/cash-management-journal-entries/cash-management-je/.
 *
 * NOT to be confused with `useSearchNetsuiteCashManagementJournalEntries`, which
 * queries `transactionLine` for the Deposit-to-Safe lookup and requires a
 * subsidiary + date. This one takes a memo and returns whole journal entries.
 *
 * Params: { memo, subsidiary, subsidiaries, limit, offset } — all optional. The
 * backend defaults `memo` to "Reverse Journal Entry - Smiles Analytics";
 * subsidiaries narrow further but are not required (unlike
 * `useSearchNetsuiteJournalEntries`, which demands one).
 *
 * Pass `limit` (and optionally `offset`) to page in NetSuite instead of pulling
 * every match down; `count` on the response is always the unpaged total.
 *
 * Exists because Cash Management posts a plain journal entry whenever an entry is
 * single-subsidiary — NetSuite rejects an advanced intercompany JE with no second
 * subsidiary — and those rows live in a different table than
 * `useSearchNetsuiteAdvancedIntercompanyJournalEntries` reads. Rows carry
 * `record_type: "journalEntry"` so a merged listing can label them.
 */
export const useSearchNetsuiteCashManagementJe = (
  { memo, subsidiary, subsidiaries, limit, offset } = {},
  options = {},
) => {
  const subList = Array.isArray(subsidiaries)
    ? subsidiaries.filter((v) => v !== null && v !== undefined && v !== "")
    : [];
  const subsidiariesCsv = subList.length > 0 ? subList.join(",") : "";

  return useQuery({
    queryKey: [
      "netsuite",
      "cash-management-journal-entries",
      "cash-management-je",
      memo ?? null,
      subsidiary ?? null,
      subsidiariesCsv,
      limit ?? null,
      offset ?? null,
    ],
    queryFn: async () => {
      const params = {};
      if (memo) params.memo = memo;
      if (subsidiariesCsv) params.subsidiaries = subsidiariesCsv;
      if (subsidiary) params.subsidiary = subsidiary;
      if (limit) {
        params.limit = limit;
        params.offset = offset || 0;
      }
      const url = buildUrlWithParams(endpoints.SEARCH_NETSUITE_CASH_MANAGEMENT_JE, params);
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

/**
 * Search NetSuite AdvIntercompanyJournalEntry rows by memo.
 * Params: { memo, limit, offset }
 *   - memo: required substring (case-insensitive LIKE)
 *   - limit/offset: optional NetSuite-side paging, newest-first. Omit `limit`
 *     and every matching row comes back, as before. `count` on the response is
 *     the unpaged total either way.
 * No subsidiary / date filter — returns all matching JEs globally.
 */
export const useSearchNetsuiteAdvancedIntercompanyJournalEntries = (
  { memo, limit, offset } = {},
  options = {},
) => {
  return useQuery({
    queryKey: [
      "netsuite",
      "advanced-intercompany-journal-entries",
      "search",
      memo,
      limit ?? null,
      offset ?? null,
    ],
    queryFn: async () => {
      const params = { memo };
      if (limit) {
        params.limit = limit;
        params.offset = offset || 0;
      }
      const url = buildUrlWithParams(
        endpoints.SEARCH_NETSUITE_ADVANCED_INTERCOMPANY_JOURNAL_ENTRIES,
        params,
      );
      const response = await api.get(url);
      return response.data;
    },
    enabled: !!memo,
    ...options,
  });
};

/**
 * Search NetSuite transactionLine rows by subsidiary + month.
 * Params: { subsidiary, subsidiaries, month }
 *   - subsidiary: single id
 *   - subsidiaries: array of ids (batched into one CSV request)
 *   - month: required "YYYY-MM" (filters linecreateddate within that month)
 * Backend groups rows by (subsidiary_id, transaction_id) and sums
 * debitforeignamount per transaction.
 */
export const useSearchNetsuiteTransactionLines = (
  { subsidiary, subsidiaries, month } = {},
  options = {},
) => {
  const list = Array.isArray(subsidiaries)
    ? subsidiaries.filter((v) => v !== null && v !== undefined && v !== "")
    : [];
  const subsidiariesCsv = list.length > 0 ? list.join(",") : "";
  const hasAny = !!subsidiary || subsidiariesCsv.length > 0;

  return useQuery({
    queryKey: ["netsuite", "transaction-lines", "search", subsidiary, subsidiariesCsv, month],
    queryFn: async () => {
      const params = {};
      if (subsidiariesCsv) params.subsidiaries = subsidiariesCsv;
      if (subsidiary) params.subsidiary = subsidiary;
      if (month) params.month = month;
      const url = buildUrlWithParams(endpoints.SEARCH_NETSUITE_TRANSACTION_LINES, params);
      const response = await api.get(url);
      return response.data;
    },
    enabled: hasAny && !!month,
    ...options,
  });
};

/**
 * Search NetSuite Deposit transactions by subsidiary + month.
 *  - subsidiary: single id
 *  - subsidiaries: array of ids (batched into one CSV request)
 *  - month: required "YYYY-MM" (filters trandate within that month ±5 days)
 *
 * Backend response is nested:
 *   { items: [ { subsidiary_id, total, line_count,
 *                dates: [ { trandate, total, line_count,
 *                           lines: [ {...} ] } ] } ],
 *     count }
 */
export const useSearchNetsuiteDeposits = (
  { subsidiary, subsidiaries, month } = {},
  options = {},
) => {
  const list = Array.isArray(subsidiaries)
    ? subsidiaries.filter((v) => v !== null && v !== undefined && v !== "")
    : [];
  const subsidiariesCsv = list.length > 0 ? list.join(",") : "";
  const hasAny = !!subsidiary || subsidiariesCsv.length > 0;

  return useQuery({
    queryKey: ["netsuite", "deposit", "search", subsidiary, subsidiariesCsv, month],
    queryFn: async () => {
      const params = {};
      if (subsidiariesCsv) params.subsidiaries = subsidiariesCsv;
      if (subsidiary) params.subsidiary = subsidiary;
      if (month) params.month = month;
      const url = buildUrlWithParams(endpoints.SEARCH_NETSUITE_DEPOSITS, params);
      const response = await api.get(url);
      return response.data;
    },
    enabled: hasAny && !!month,
    ...options,
  });
};

// Subsidiaries per Deposit Sheet NetSuite request. Batches are cut from the FULL
// sorted subsidiary list, so a clinic always lands in the same batch whatever
// else is selected — the request (and its SuiteQL text) stays byte-identical and
// hits both this cache and the server's SuiteQL cache.
const DEPOSIT_SHEET_SUBSIDIARY_BATCH = 20;
// Matches the server-side SuiteQL transactional TTL (SUITEQL_CACHE_TTL, 1h).
const DEPOSIT_SHEET_NETSUITE_STALE_MS = 60 * 60 * 1000;

function cleanIds(ids) {
  return Array.isArray(ids) ? ids.filter((v) => v !== null && v !== undefined && v !== "") : [];
}

// The fixed batches (CSV strings) that contain at least one wanted subsidiary.
function subsidiaryBatchesFor(wanted, all) {
  const wantedSet = new Set(cleanIds(wanted).map(String));
  if (wantedSet.size === 0) return [];
  const universe = Array.from(new Set([...cleanIds(all), ...cleanIds(wanted)].map(String))).sort(
    (a, b) => Number(a) - Number(b),
  );
  const batches = [];
  for (let i = 0; i < universe.length; i += DEPOSIT_SHEET_SUBSIDIARY_BATCH) {
    const batch = universe.slice(i, i + DEPOSIT_SHEET_SUBSIDIARY_BATCH);
    if (batch.some((id) => wantedSet.has(id))) batches.push(batch.join(","));
  }
  return batches;
}

// One query per (month, subsidiary batch). The NetSuite search endpoints only
// take one `month`; spanning months is done by merging in `combine`. Toggling a
// month or clinic only fetches the (month, batch) pairs not already cached.
function useNetsuiteMonthQueries(
  keyPrefix,
  endpoint,
  { subsidiaries, allSubsidiaries, months },
  options,
  combine,
) {
  const batches = subsidiaryBatchesFor(subsidiaries, allSubsidiaries);
  const { enabled: enabledOption = true, ...queryOptions } = options;
  return useQueries({
    queries: (months || []).filter(Boolean).flatMap((month) =>
      batches.map((subsidiariesCsv) => ({
        queryKey: ["netsuite", ...keyPrefix, "batch", subsidiariesCsv, month],
        queryFn: async () => {
          const url = buildUrlWithParams(endpoint, { subsidiaries: subsidiariesCsv, month });
          const response = await api.get(url);
          return response.data;
        },
        enabled: enabledOption,
        staleTime: DEPOSIT_SHEET_NETSUITE_STALE_MS,
        gcTime: 2 * DEPOSIT_SHEET_NETSUITE_STALE_MS,
        ...queryOptions,
      })),
    ),
    combine,
  });
}

/**
 * Multi-month `useSearchNetsuiteTransactionLines`. Params:
 * `{ subsidiaries, allSubsidiaries, months }` — `allSubsidiaries` is every id the
 * user can see (fixes the batch boundaries; see `subsidiaryBatchesFor`). Results
 * include the whole batch, so callers pick their subsidiaries out by id.
 * Each month's request is
 * widened ±5 days server-side, so adjacent months return some of the same
 * transactions — those are de-duplicated on (subsidiary_id, transaction_id).
 * Returns `{ data: { items, count }, isFetching }`.
 */
export const useSearchNetsuiteTransactionLinesForMonths = (params = {}, options = {}) =>
  useNetsuiteMonthQueries(
    ["transaction-lines", "search"],
    endpoints.SEARCH_NETSUITE_TRANSACTION_LINES,
    params,
    options,
    combineTransactionLineMonths,
  );

function combineTransactionLineMonths(results) {
  const isFetching = results.some((r) => r.isFetching);
  if (results.every((r) => !r.data)) return { data: undefined, isFetching };
  const seen = new Set();
  const items = [];
  results.forEach((r) => {
    (r.data?.items || []).forEach((tx) => {
      const key = `${tx?.subsidiary_id}:${tx?.transaction_id}`;
      if (tx?.transaction_id != null && seen.has(key)) return;
      seen.add(key);
      items.push(tx);
    });
  });
  return { data: { items, count: items.length }, isFetching };
}

/**
 * Multi-month `useSearchNetsuiteDeposits`. Months don't overlap server-side, so
 * each subsidiary's `dates` are concatenated and its totals summed.
 * Returns `{ data: { items, count }, isFetching }`.
 */
export const useSearchNetsuiteDepositsForMonths = (params = {}, options = {}) =>
  useNetsuiteMonthQueries(
    ["deposit", "search"],
    endpoints.SEARCH_NETSUITE_DEPOSITS,
    params,
    options,
    combineDepositMonths,
  );

function combineDepositMonths(results) {
  const isFetching = results.some((r) => r.isFetching);
  if (results.every((r) => !r.data)) return { data: undefined, isFetching };
  const bySid = new Map();
  let count = 0;
  results.forEach((r) => {
    count += Number(r.data?.count) || 0;
    (r.data?.items || []).forEach((item) => {
      const prev = bySid.get(item?.subsidiary_id);
      if (!prev) {
        bySid.set(item?.subsidiary_id, { ...item, dates: [...(item?.dates || [])] });
        return;
      }
      prev.total += Number(item?.total) || 0;
      prev.line_count += Number(item?.line_count) || 0;
      prev.dates.push(...(item?.dates || []));
    });
  });
  const items = Array.from(bySid.values());
  items.forEach((it) =>
    it.dates.sort((a, b) => (a?.trandate || "").localeCompare(b?.trandate || "")),
  );
  return { data: { items, count }, isFetching };
}

/**
 * Fetch NetSuite accounts (id + displaynamewithhierarchy).
 *  - { id }  → single lookup
 *  - { ids: [...] } → batched lookup (one request)
 *  - neither → list up to 100
 */
export const useNetsuiteAccounts = ({ id, ids, acctnumbers } = {}, options = {}) => {
  const list = Array.isArray(ids)
    ? ids.filter((v) => v !== null && v !== undefined && v !== "")
    : [];
  const idsCsv = list.length > 0 ? list.join(",") : "";
  const acctList = Array.isArray(acctnumbers)
    ? acctnumbers.filter(Boolean)
    : acctnumbers
      ? [acctnumbers]
      : [];
  const acctnumbersCsv = acctList.join(",");

  return useQuery({
    queryKey: ["netsuite", "accounts", id ?? "none", idsCsv || "all", acctnumbersCsv || ""],
    queryFn: async () => {
      const params = {};
      if (idsCsv) params.ids = idsCsv;
      if (id) params.id = id;
      if (acctnumbersCsv) params.acctnumbers = acctnumbersCsv;
      const url = buildUrlWithParams(endpoints.GET_NETSUITE_ACCOUNTS, params, { clean: true });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

/**
 * Fetch NetSuite "Bank Balance" accounts.
 * Returns id, subsidiary, fullname for every account whose fullname
 * contains "Bank Balance".
 */
/**
 * Fetch BankingStatement rows for one or more account IDs over a date range.
 *  - ids: number[]   — account IDs to query
 *  - from: string    — start date "YYYY-MM-DD" (optional)
 *  - to:   string    — end date   "YYYY-MM-DD" (optional)
 */
export const useNetsuiteBankingStatements = ({ ids = [], asof } = {}, options = {}) => {
  const idsKey = [...ids].sort((a, b) => a - b).join(",");
  return useQuery({
    queryKey: ["netsuite", "banking-statements", idsKey, asof ?? null],
    queryFn: async () => {
      const params = { ids: ids.join(","), asof };
      const response = await api.get(endpoints.GET_NETSUITE_BANKING_STATEMENTS, { params });
      return response.data;
    },
    enabled: ids.length > 0 && !!asof,
    // The bank feed posts once a day, in the morning, and a statement already
    // filed never changes — so re-fetching within a session can only return what
    // is already held. Overrides the 5-minute client default (see queryClient.js)
    // to stop every remount and date toggle from making a round trip. Callers can
    // still override via `options`.
    staleTime: 8 * 60 * 60 * 1000, // 8h, matching SUITEQL_CACHE_TTL_BANKING_STATEMENT
    gcTime: 8 * 60 * 60 * 1000,
    ...options,
  });
};

/**
 * Running end-of-day NetSuite GL balance for one or more accounts, per day
 * across a date range.
 *  - ids:  number[] — account IDs to query
 *  - from: string   — first day "YYYY-MM-DD"
 *  - to:   string   — last day  "YYYY-MM-DD"
 *
 * Returns { from, to, count, items: [{ account, opening_balance,
 * ending_balance, days: [{ date, change, balance }] }] }. Every account
 * reports every day in the range — days with no activity carry the previous
 * balance forward — so the series can be read straight onto a day grid without
 * filling gaps here.
 *
 * Unlike `useNetsuiteBankingStatements` this sums transaction lines rather than
 * reading filed bank statements, so it reflects what is posted in NetSuite, not
 * what the bank has confirmed.
 */
export const useNetsuiteBankBalances = ({ ids = [], from, to } = {}, options = {}) => {
  // Sorted, matching what the view does server-side: the account order a caller
  // happens to pass must not fragment either cache.
  const idsKey = [...ids].sort((a, b) => a - b).join(",");

  // Nothing can be posted to a date that has not happened yet, so a window
  // reaching into the future asks the ledger to scan days that could only
  // repeat today's figure. Capping the request at today keeps it — and its
  // cache key — identical for every grid whose window merely extends further
  // ahead, instead of minting a fresh full-history scan per end-date.
  // ISO dates compare correctly as strings, so no parsing is needed.
  const today = todayISO();
  const cappedTo = to && to > today ? today : to;
  // A window starting after today has nothing to ask for at all.
  const wholeWindowIsFuture = !!from && from > today;

  return useQuery({
    queryKey: ["netsuite", "bank-balances", idsKey, from ?? null, cappedTo ?? null],
    queryFn: async () => {
      const response = await api.get(endpoints.GET_NETSUITE_BANK_BALANCES, {
        params: { ids: idsKey, from, to: cappedTo },
      });
      return response.data;
    },
    // Summing the ledger is the slowest call on the cash-flow page and the
    // answer moves only as transactions post, so this holds far longer than the
    // 5-minute client default (see queryClient.js) — a remount or a card toggle
    // must not re-run it. Callers can still override via `options`.
    staleTime: 60 * 60 * 1000, // 1h, matching the server-side transactional TTL
    gcTime: 60 * 60 * 1000,
    ...options,
    // After the spread, deliberately: a caller's `enabled` narrows this query,
    // it must not widen it back into a request the endpoint would reject (a
    // future-only window inverts to from > to, which is a 400).
    enabled:
      (options.enabled ?? true) && ids.length > 0 && !!from && !!cappedTo && !wholeWindowIsFuture,
  });
};

/**
 * Build the shared `ids` + day-selector query params for the two
 * reconciliation endpoints, plus the cache key fragment that goes with them.
 *
 * Both accept days two ways — an arbitrary `dates` list or a contiguous
 * `from`/`to` range — and reject being given both at once, so the choice is
 * made here once rather than in each hook.
 *
 * Accounts and dates are sorted, matching what the views do server-side: the
 * order a caller happens to pass them in must not fragment either cache.
 */
function bankRecParams({ ids = [], dates = [], from, to }) {
  const idsKey = [...ids].sort((a, b) => a - b).join(",");
  const useDates = dates.length > 0;
  const datesKey = useDates ? [...new Set(dates)].sort().join(",") : null;

  const params = useDates ? { ids: idsKey, dates: datesKey } : { ids: idsKey, from, to };

  return {
    params,
    idsKey,
    datesKey,
    // A day selector of some kind is required; sending neither is a 400. So is
    // sending both, which `params` above already prevents.
    hasWindow: useDates || (!!from && !!to),
  };
}

/**
 * Bank-feed transactions — what the *bank* said happened, imported daily into
 * NetSuite's BankingTransaction table.
 *  - ids:   number[] — bank account IDs to query
 *  - dates: string[] — specific days "YYYY-MM-DD", any number of them
 *  - from/to: string — or a contiguous range instead ("YYYY-MM-DD")
 *
 * Pass `dates` or `from`+`to`, not both — `dates` wins if both are given, and
 * the endpoint rejects receiving the pair.
 *
 * Returns { dates | from & to, count, items: [{ id, account, transactiondate,
 * transactiontype, amount, memo }] }. Rows for several accounts arrive
 * interleaved in one list, ordered by account then date, so group on `account`
 * to split them.
 *
 * This is one side of a reconciliation; `useNetsuiteLedgerTransactions` is the
 * other. Rows appearing here with no counterpart there are what the bank sent
 * that NetSuite has not booked.
 */
// Query options for one bank-feed request, shared by the single and batched hooks
// so both land on the same react-query cache entries.
function bankFeedQueryOptions({ ids = [], dates = [], from, to }, options = {}) {
  const { params, idsKey, datesKey, hasWindow } = bankRecParams({ ids, dates, from, to });
  return {
    queryKey: [
      "netsuite",
      "bank-feed-transactions",
      idsKey,
      datesKey,
      datesKey ? null : (from ?? null),
      datesKey ? null : (to ?? null),
    ],
    queryFn: async () => {
      const response = await api.get(endpoints.GET_NETSUITE_BANK_FEED_TRANSACTIONS, { params });
      return response.data;
    },
    // A feed can be re-imported or corrected during the day, so this does not
    // take the long statement TTL — it matches the server-side transactional
    // one, past which a refetch is at least capable of returning something new.
    // Callers can still override via `options`.
    //
    // Deliberately *not* capped to today the way `useNetsuiteBankBalances` is:
    // nothing can be *posted* to a future date, but a transaction can be
    // *dated* in one, so a future-reaching window here is a real question.
    staleTime: 60 * 60 * 1000, // 1h
    gcTime: 60 * 60 * 1000,
    ...options,
    // After the spread, deliberately: a caller's `enabled` may narrow this
    // query but must not widen it into a request the endpoint would reject.
    enabled: (options.enabled ?? true) && ids.length > 0 && hasWindow,
  };
}

export const useNetsuiteBankFeedTransactions = (
  { ids = [], dates = [], from, to } = {},
  options = {},
) => useQuery(bankFeedQueryOptions({ ids, dates, from, to }, options));

// Matches ACCOUNTS_PER_SCAN in the backend's banking_statements/services.py:
// one request = exactly one SuiteQL scan = one server-side cache entry.
const BANK_FEED_BATCH_SIZE = 40;

// Module-level so react-query can memoize the combined result between renders.
const combineBankFeedBatches = (results) => ({
  items: results.flatMap((r) => r.data?.items ?? []),
  incomplete: results.some((r) => r.data?.incomplete),
  isLoading: results.some((r) => r.isLoading),
  isSuccess: results.length > 0 && results.every((r) => r.isSuccess),
  isError: results.some((r) => r.isError),
  error: results.find((r) => r.error)?.error ?? null,
});

/**
 * Bank-feed transactions for a LARGE, STABLE account set, split into fixed
 * batches. Pass the same superset every time (e.g. every RBC clinic account)
 * and filter client-side: then each batch's request is identical no matter
 * what the user selected, so it is served from the react-query cache and the
 * backend's Redis SuiteQL cache instead of costing a new NetSuite query per
 * selection. Ids are sorted before batching for the same reason.
 *
 * Returns { items, isLoading, isSuccess, isError, error, incomplete }.
 */
export const useNetsuiteBankFeedTransactionsBatched = (
  { ids = [], dates = [], from, to } = {},
  options = {},
) => {
  const sorted = [...new Set(ids.map(String))].sort((a, b) => Number(a) - Number(b));
  const batches = [];
  for (let i = 0; i < sorted.length; i += BANK_FEED_BATCH_SIZE) {
    batches.push(sorted.slice(i, i + BANK_FEED_BATCH_SIZE));
  }

  return useQueries({
    queries: batches.map((batch) => bankFeedQueryOptions({ ids: batch, dates, from, to }, options)),
    combine: combineBankFeedBatches,
  });
};

/**
 * Ledger transactions — what *NetSuite* has booked against the same accounts.
 * Same arguments as `useNetsuiteBankFeedTransactions`.
 *
 * Returns { dates | from & to, count, items: [{ transaction, line, account,
 * trandate, foreigntotal, memo, cleared }] }.
 *
 * One row per transaction *line* on a requested account, not per transaction:
 * `cleared` is a line-level flag, so it cannot be collapsed to the header. A
 * transaction posting twice to the same account therefore appears twice, and
 * `foreigntotal` — a header column — repeats across those rows. Summing
 * `foreigntotal` over `items` double-counts; it is the document total to show
 * beside a line, not a per-line amount. Use `useNetsuiteBankBalances` for
 * figures that add up.
 */
export const useNetsuiteLedgerTransactions = (
  { ids = [], dates = [], from, to } = {},
  options = {},
) => {
  const { params, idsKey, datesKey, hasWindow } = bankRecParams({ ids, dates, from, to });

  return useQuery({
    queryKey: [
      "netsuite",
      "ledger-transactions",
      idsKey,
      datesKey,
      datesKey ? null : (from ?? null),
      datesKey ? null : (to ?? null),
    ],
    queryFn: async () => {
      const response = await api.get(endpoints.GET_NETSUITE_LEDGER_TRANSACTIONS, { params });
      return response.data;
    },
    // A posted transaction's memo or cleared flag can change during the day,
    // so this matches the server-side transactional TTL rather than holding
    // longer. Callers can still override via `options`.
    staleTime: 60 * 60 * 1000, // 1h
    gcTime: 60 * 60 * 1000,
    ...options,
    enabled: (options.enabled ?? true) && ids.length > 0 && hasWindow,
  });
};

/**
 * Reconcile scheduled payments against what actually happened at the bank.
 *
 *  - clinic: string   — clinic code(s), CSV. What ClinicCashFlow holds.
 *  - bankAccount: string|number[] — or account PKs, for the entity grid.
 *  - from/to: string  — the visible window, "YYYY-MM-DD"
 *  - tolerance: how far amounts may differ before a match is ASSERTED
 *    (default $0.01 server-side). Anything inside the wider suggest band comes
 *    back under `suggestions` instead, each carrying `clears_at_tolerance` —
 *    the value this would have to be for that match to fire. Read those off
 *    real data rather than guessing a threshold up front.
 *  - windowDays: how far apart the dates may be (default 7 server-side).
 *  - refreshNonce: bump to force a recompute. A NONCE rather than a boolean,
 *    because there are two caches to get past: the number joins the query key
 *    (so react-query cannot serve its own stored answer) and sends `refresh=1`
 *    (so the server recomputes instead of returning its 6h entry). Both are
 *    needed — either alone leaves the user looking at the same figures they
 *    just asked to have refreshed.
 *
 * Returns { from, to, today, params, counts, items, unmatched_transactions,
 * accounts_without_netsuite_id }, where each item is one occurrence with
 * `cleared`, `cleared_date`, `cleared_note`, `display_date` and
 * `rolled_forward_from`.
 *
 * Matching is a heuristic — an occurrence and a NetSuite row share no key, so
 * they are paired on amount and date proximity. Treat `cleared` as strongly
 * suggested rather than asserted; `match.confidence` and `suggestions` are
 * there so a screen can say how sure it is.
 */
export const useSchedulePaymentReconciliation = (
  {
    clinic,
    bankAccount,
    from,
    to,
    tolerance,
    suggestTolerance,
    windowDays,
    lookbackDays,
    refreshNonce = 0,
  } = {},
  options = {},
) => {
  const clinicKey = Array.isArray(clinic) ? [...clinic].sort().join(",") : (clinic ?? null);
  const accountKey = Array.isArray(bankAccount)
    ? [...bankAccount].sort((a, b) => a - b).join(",")
    : (bankAccount ?? null);

  const params = {};
  if (clinicKey) params.clinic = clinicKey;
  if (accountKey) params.bank_account = accountKey;
  if (from) params.from = from;
  if (to) params.to = to;
  if (tolerance != null) params.tolerance = tolerance;
  if (suggestTolerance != null) params.suggest_tolerance = suggestTolerance;
  if (windowDays != null) params.window_days = windowDays;
  if (lookbackDays != null) params.lookback_days = lookbackDays;

  // `refresh=1` must be spent ONCE, on the fetch the user actually asked for.
  //
  // It cannot simply follow `refreshNonce`, because the nonce stays raised for
  // the rest of the session: after one click, every later fetch — a changed
  // window, a changed clinic, a remount — would carry refresh=1 and recompute
  // from NetSuite, so a single click quietly disabled the 6h server cache until
  // reload. The ref records the highest nonce already spent, so the bypass
  // applies to that one refresh and nothing after it.
  const spentNonceRef = useRef(0);

  return useQuery({
    queryKey: [
      "schedule-payment",
      "reconcile",
      clinicKey,
      accountKey,
      from ?? null,
      to ?? null,
      tolerance ?? null,
      suggestTolerance ?? null,
      windowDays ?? null,
      lookbackDays ?? null,
      refreshNonce,
    ],
    queryFn: async () => {
      const bypassCache = refreshNonce > spentNonceRef.current;
      if (bypassCache) spentNonceRef.current = refreshNonce;
      const response = await api.get(endpoints.RECONCILE_SCHEDULE_PAYMENTS, {
        params: bypassCache ? { ...params, refresh: 1 } : params,
      });
      return response.data;
    },
    // Two SuiteQL scans per call, so this holds as long as the transactional
    // NetSuite cache behind it — a remount or a card toggle must not re-run it.
    // Callers can still override via `options`.
    staleTime: 60 * 60 * 1000, // 1h
    gcTime: 60 * 60 * 1000,
    // A failed reconciliation must not blank the grid: the cash-flow page is
    // fully usable without it, minus the cleared markers.
    placeholderData: (prev) => prev,
    ...options,
    // After the spread, deliberately: a caller's `enabled` may narrow this
    // query but must not widen it into a request the endpoint would reject
    // (no window, or no account/clinic filter, are both 400s).
    enabled: (options.enabled ?? true) && !!(clinicKey || accountKey) && !!from && !!to,
  });
};

/**
 * Record human decisions about whether scheduled payments cleared the bank.
 *
 * Takes a list of {schedule, original_date, cleared, cleared_date, amount,
 * source, matched_reference, cleared_note}. Upserts on (schedule,
 * original_date), one transaction, all-or-nothing.
 *
 * `cleared: false` is a real decision — it rejects a match the reconciler would
 * otherwise keep asserting — so both accepting and rejecting come through here.
 *
 * On success the reconciliation is invalidated rather than patched: a decision
 * frees the NetSuite row it was holding, which can let a DIFFERENT occurrence
 * match, so the whole window has to be recomputed. Guessing at that locally
 * would put the grid out of step with the server.
 */
/**
 * Apply recorded decisions to a cached reconcile payload, in place.
 *
 * A decision's effect on the occurrence it names is fully known from the
 * response — cleared or not, by whom, against which NetSuite row — so it can be
 * written straight into the cache. That matters because the alternative,
 * invalidating and refetching, costs a full reconciliation: measured at 5.4s
 * across 36 accounts even with the NetSuite rows already cached, and the
 * per-row "Didn't clear" button would pay it on every single click.
 *
 * What this CANNOT know: a decision releases the NetSuite row that occurrence
 * was holding, and that row may then explain a DIFFERENT occurrence. Only the
 * server can work that out. So the patch is deliberately narrow — it touches
 * only the occurrences that were decided, and leaves everything else exactly as
 * the last reconciliation returned it. The next natural refetch (a changed
 * window, or the "Reconcile now" button) reconciles the rest.
 */
function applyDecisions(payload, rows) {
  if (!payload?.items?.length || !rows?.length) return payload;

  const byKey = new Map(rows.map((r) => [`${r.schedule}:${r.original_date}`, r]));
  let touched = 0;

  const items = payload.items.map((item) => {
    const decided = byKey.get(`${item.schedule}:${item.original_date}`);
    if (!decided) return item;
    touched += 1;
    return {
      ...item,
      cleared: decided.cleared,
      cleared_date: decided.cleared_date ?? null,
      cleared_note: decided.cleared_note ?? "",
      cleared_source: "decided",
      // The inferred match is gone, not merely overridden: a decided occurrence
      // takes no part in matching, so leaving `match` in place would keep
      // offering it for confirmation and keep drawing its confidence chip.
      match: null,
      suggestions: [],
      uncleared_reason: decided.cleared ? null : "rejected",
      decision: {
        cleared: decided.cleared,
        source: decided.source,
        matched_reference: decided.matched_reference ?? "",
        confirmed_by: decided.confirmed_by_email ?? null,
        confirmed_at: decided.confirmed_at ?? null,
      },
    };
  });

  if (!touched) return payload;

  // Only the counts a DECISION can change are recomputed. The scan-derived ones
  // (candidates, unmatched_transactions, not_yet_due) still describe the last
  // reconciliation, which is exactly what they are — recomputing them here
  // would be inventing figures no scan produced.
  const counts = {
    ...payload.counts,
    cleared: items.filter((i) => i.cleared).length,
    uncleared: items.filter((i) => !i.cleared).length,
    decided: items.filter((i) => i.decision).length,
    decided_cleared: items.filter((i) => i.decision && i.decision.cleared).length,
    decided_not_cleared: items.filter((i) => i.decision && !i.decision.cleared).length,
    with_suggestions: items.filter((i) => i.suggestions?.length).length,
  };

  return { ...payload, items, counts };
}

/**
 * Record human decisions about whether scheduled payments cleared the bank.
 *
 * Takes a list of {schedule, original_date, cleared, cleared_date, amount,
 * source, matched_reference, cleared_note}. Upserts on (schedule,
 * original_date), one transaction, all-or-nothing.
 *
 * `cleared: false` is a real decision — it rejects a match the reconciler would
 * otherwise keep asserting — so accepting and rejecting both come through here.
 *
 * On success the cached reconciliation is PATCHED rather than invalidated. See
 * applyDecisions for why, and for what that deliberately does not attempt.
 */
export const useConfirmScheduleClearances = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (decisions) => {
      const response = await api.post(endpoints.CONFIRM_SCHEDULE_CLEARED, decisions);
      return response.data;
    },
    ...options,
    onSuccess: (rows, variables, context) => {
      // Every cached reconcile window, not just the visible one: the same
      // occurrence appears in a 30-day and a 60-day window, and leaving the
      // other one stale would make a decision appear to un-happen on switching.
      queryClient.setQueriesData({ queryKey: ["schedule-payment", "reconcile"] }, (old) =>
        applyDecisions(old, rows),
      );
      options.onSuccess?.(rows, variables, context);
    },
  });
};

export const useNetsuiteBankBalanceAccounts = ({ id, ids } = {}, options = {}) => {
  const idsKey = ids ? [...ids].sort().join(",") : null;
  return useQuery({
    queryKey: ["netsuite", "accounts", "bank-balance", id ?? null, idsKey],
    queryFn: async () => {
      let params;
      if (ids && ids.length > 0) {
        params = { ids: ids.join(",") };
      } else if (id != null) {
        params = { id };
      }
      const response = await api.get(endpoints.GET_NETSUITE_BANK_BALANCE_ACCOUNTS, { params });
      return response.data;
    },
    ...options,
  });
};

/**
 * Fetch NetSuite subsidiaries (id + fullname).
 *  - { id }  → single lookup
 *  - { ids: [...] } → batched lookup (one request)
 *  - neither → list up to 100
 */
export const useNetsuiteSubsidiaries = ({ id, ids } = {}, options = {}) => {
  const list = Array.isArray(ids)
    ? ids.filter((v) => v !== null && v !== undefined && v !== "")
    : [];
  const idsCsv = list.length > 0 ? list.join(",") : "";

  return useQuery({
    queryKey: ["netsuite", "subsidiaries", id ?? "none", idsCsv || "all"],
    queryFn: async () => {
      const params = {};
      if (idsCsv) params.ids = idsCsv;
      if (id) params.id = id;
      const url = buildUrlWithParams(endpoints.GET_NETSUITE_SUBSIDIARIES, params, { clean: true });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

/**
 * Fetch NetSuite AccountingPeriod records.
 *  - { periodname: "Jun 2026" } → look up by exact periodname (case-insensitive)
 *  - {}                         → return the 50 most-recent periods
 */
export const useNetsuiteAccountingPeriods = ({ periodname } = {}, options = {}) => {
  return useQuery({
    queryKey: ["netsuite", "accounting-periods", periodname ?? "all"],
    queryFn: async () => {
      const params = {};
      if (periodname) params.periodname = periodname;
      const url = buildUrlWithParams(endpoints.GET_NETSUITE_ACCOUNTING_PERIODS, params, {
        clean: true,
      });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

/**
 * Fetch NetSuite vendors/entities (id + fullname + entitytitle).
 *  - { id }  → single lookup
 *  - { ids: [...] } → batched lookup (one request)
 * Backend requires at least one of `id` or `ids`.
 */
export const useNetsuiteVendors = ({ id, ids } = {}, options = {}) => {
  const list = Array.isArray(ids)
    ? ids.filter((v) => v !== null && v !== undefined && v !== "")
    : [];
  const idsCsv = list.length > 0 ? list.join(",") : "";
  const hasAny = !!id || idsCsv.length > 0;

  return useQuery({
    queryKey: ["netsuite", "vendors", id ?? "none", idsCsv || "none"],
    queryFn: async () => {
      const params = {};
      if (idsCsv) params.ids = idsCsv;
      if (id) params.id = id;
      const url = buildUrlWithParams(endpoints.GET_NETSUITE_VENDORS, params, { clean: true });
      const response = await api.get(url);
      return response.data;
    },
    enabled: hasAny,
    ...options,
  });
};

// ==================== CASH TRANSACTION ====================

/**
 * Derived cash balance for a clinic.
 *   filters: { clinic: "ON-T-EVD", as_of?: "YYYY-MM-DD" }
 *   returns: { clinic, as_of, balance } where balance is a string decimal.
 */
export const useCashBalance = (filters = {}, options = {}) => {
  const enabled = !!filters.clinic && options.enabled !== false;
  return useQuery({
    queryKey: ["cash-transaction", "balance", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_CASH_TRANSACTION_BALANCE, filters, {
        clean: true,
      });
      const response = await api.get(url);
      return response.data;
    },
    enabled,
    ...options,
  });
};

/**
 * List cash transactions. Supported filters:
 *   - clinic:        clinic code — matches source OR dest
 *   - source_clinic: clinic code (strict)
 *   - dest_clinic:   clinic code (strict)
 *   - status:        pending | confirmed | rejected | voided
 *   - kind:          deposit | withdrawal | transfer | adjustment
 *   - date_from / date_to: YYYY-MM-DD (filter by occurred_on)
 *   - mine: "true"   |  as: "giver" | "receiver"
 *   - limit / offset
 */
export const useCashTransactions = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["cash-transaction", "list", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_ALL_CASH_TRANSACTIONS, filters, { clean: true });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

export const useCashTransaction = (id, options = {}) => {
  return useQuery({
    queryKey: ["cash-transaction", id],
    queryFn: async () => {
      const response = await api.get(endpoints.GET_CASH_TRANSACTION(id));
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};

export const useCashBalanceDaily = (params = {}, options = {}) => {
  const enabled = !!params.clinic && !!params.year && !!params.month && options.enabled !== false;
  return useQuery({
    queryKey: ["cash-transaction", "balance-daily", params],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_CASH_TRANSACTION_BALANCE_DAILY, params, {
        clean: true,
      });
      const response = await api.get(url);
      return response.data;
    },
    enabled,
    ...options,
  });
};

/**
 * `useCashBalanceDaily` for several clinics × { year, month } pairs, one cached
 * request per clinic-month (keyed like the single hook) so changing the clinic
 * or month selection only fetches what isn't loaded yet.
 * Returns `{ data: { byClinic: { CLINIC: days[] } }, isLoading }`.
 */
export const useCashBalanceDailyForMonths = ({ clinics = [], months = [] } = {}, options = {}) => {
  const list = Array.from(new Set((clinics || []).filter(Boolean)));
  return useQueries({
    queries: list.flatMap((clinic) =>
      months.map(({ year, month }) => {
        const params = { clinic, year, month };
        return {
          queryKey: ["cash-transaction", "balance-daily", params],
          queryFn: async () => {
            const url = buildUrlWithParams(endpoints.GET_CASH_TRANSACTION_BALANCE_DAILY, params, {
              clean: true,
            });
            const response = await api.get(url);
            return response.data;
          },
          enabled: !!year && !!month && options.enabled !== false,
        };
      }),
    ),
    combine: combineCashBalanceDaily,
  });
};

function combineCashBalanceDaily(results) {
  const isLoading = results.some((r) => r.isLoading);
  if (results.every((r) => !r.data)) return { data: undefined, isLoading };
  const byClinic = {};
  results.forEach((r) => {
    (r.data?.results || []).forEach(({ clinic, days }) => {
      byClinic[clinic] = [...(byClinic[clinic] || []), ...(days || [])];
    });
  });
  return { data: { byClinic }, isLoading };
}

export const useCashTransactionEvents = (id, options = {}) => {
  return useQuery({
    queryKey: ["cash-transaction", id, "events"],
    queryFn: async () => {
      const response = await api.get(endpoints.GET_CASH_TRANSACTION_EVENTS(id));
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};

export const useCreateCashTransaction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(endpoints.CREATE_CASH_TRANSACTION, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cash-transaction"] });
    },
  });
};

/**
 * PATCH a cash transaction. The only mutable field is `status` (with an
 * optional `note` recorded as the audit-event comment).
 *
 *   useUpdateCashTransaction().mutate({ id, status: 'confirmed', note: '...' })
 *   useUpdateCashTransaction().mutate({ id, status: 'rejected' })
 *   useUpdateCashTransaction().mutate({ id, status: 'voided' })
 */
export const useUpdateCashTransaction = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status, note } = {}) => {
      const payload = { status };
      if (note) payload.note = note;
      const response = await api.patch(endpoints.UPDATE_CASH_TRANSACTION(id), payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cash-transaction"] });
    },
  });
};

/**
 * Users that can act as giver/receiver for a cash transaction at a clinic.
 *   filters: { clinic: "ON-T-EVD", exclude_self?: "true"|"false" }
 */
export const useEligibleCashUsers = (filters = {}, options = {}) => {
  const enabled = !!filters.clinic;
  return useQuery({
    queryKey: ["cash-transaction", "eligible-users", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_CASH_TRANSACTION_ELIGIBLE_USERS, filters, {
        clean: true,
      });
      const response = await api.get(url);
      return response.data;
    },
    enabled,
    ...options,
  });
};

/**
 * Deposit the requesting user's full personal cash on hand to the clinic safe.
 * Zeroes out their on-hand balance — the cash is settled and removed from all
 * running totals.
 *
 *   useDepositCashToSafe().mutate({ clinic_code: "ON-T-EVD", occurred_on: "2026-06-03" })
 */
export const useDepositCashToSafe = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // post_journal_entry: the backend builds and posts the deposit JE itself
    // (Area Manager / Practitioner Lead, who cannot use the NetSuite JE
    // endpoints). Admin posts its own edited JE first and leaves this off.
    mutationFn: async ({ clinic_code, occurred_on, post_journal_entry } = {}) => {
      const response = await api.post(endpoints.DEPOSIT_CASH_TO_SAFE, {
        clinic_code,
        occurred_on,
        ...(post_journal_entry ? { post_journal_entry: true } : {}),
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cash-transaction"] });
    },
  });
};

// ─── Schedule Payment ─────────────────────────────────────────────────────────

/**
 * List scheduled payments. Supported filters:
 *   - clinic:       clinic id
 *   - status:       active | cancelled  (rarely useful — a cancelled schedule
 *                   carries an `end_date` and stops generating occurrences
 *                   there, so the cash-flow grid asks for BOTH and lets the
 *                   dates decide. Filtering to active hides paid history.)
 *   - expense_type: rent | loan | interest | ...
 *   - limit / offset
 */
export const useSchedulePayments = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["schedule-payment", "list", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_ALL_SCHEDULE_PAYMENTS, filters, { clean: true });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

export const useCreateSchedulePayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(endpoints.CREATE_SCHEDULE_PAYMENT, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-payment"] });
    },
  });
};

export const useUpdateSchedulePayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }) => {
      const response = await api.patch(endpoints.UPDATE_SCHEDULE_PAYMENT(id), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-payment"] });
    },
  });
};

export const useDeleteSchedulePayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(endpoints.DELETE_SCHEDULE_PAYMENT(id));
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-payment"] });
    },
  });
};

/* ── Statement Payment ────────────────────────────────────────────────────────
 * Recurring credit-card statements and the plan that pays them down. Keyed on
 * two bank accounts (paying bank -> card); filter by `entity_id` (the card's
 * owner), `to_card_account`, `from_bank_account`, or `status`.
 */

export const useStatementPayments = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["statement-payment", "list", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_ALL_STATEMENT_PAYMENTS, filters, {
        clean: true,
      });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

export const useCreateStatementPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(endpoints.CREATE_STATEMENT_PAYMENT, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["statement-payment"] });
    },
  });
};

export const useUpdateStatementPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }) => {
      const response = await api.patch(endpoints.UPDATE_STATEMENT_PAYMENT(id), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["statement-payment"] });
    },
  });
};

export const useDeleteStatementPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(endpoints.DELETE_STATEMENT_PAYMENT(id));
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["statement-payment"] });
    },
  });
};

/* ── Statement Plan ───────────────────────────────────────────────────────────
 * The saved template a statement is created FROM: the two accounts, the payment
 * plan, and the statement/due DAY of the month. No balance and no dates, so one
 * plan serves every month — the user supplies only the amount.
 *
 * At most one plan per card, enforced by the database. Same filters as
 * statement-payment (`entity_id`, `to_card_account`, `from_bank_account`,
 * `status`) minus the date bounds, which a plan has nothing to answer.
 */

export const useStatementPlans = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["statement-plan", "list", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_ALL_STATEMENT_PLANS, filters, {
        clean: true,
      });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

export const useCreateStatementPlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(endpoints.CREATE_STATEMENT_PLAN, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["statement-plan"] });
    },
  });
};

export const useUpdateStatementPlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }) => {
      const response = await api.patch(endpoints.UPDATE_STATEMENT_PLAN(id), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["statement-plan"] });
    },
  });
};

export const useDeleteStatementPlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(endpoints.DELETE_STATEMENT_PLAN(id));
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["statement-plan"] });
    },
  });
};

// One-off exceptions (move / skip / amount-override a single occurrence)

export const useCreateSchedulePaymentException = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(endpoints.CREATE_SCHEDULE_PAYMENT_EXCEPTION, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-payment"] });
    },
  });
};

/**
 * Upsert many exceptions in ONE request. Identity is (schedule, original_date),
 * so re-sending a move settles onto the same row rather than colliding with it.
 *
 * For the cash-flow grid, which moves every payment landing on a closed day at
 * once: as single POSTs that is a round trip per payment, and a partial failure
 * leaves no way to tell what applied. The batch is one transaction server-side.
 *
 * @param {{schedule: number, original_date: string, new_date: string|null,
 *          amount?: number}[]} rows
 */
export const useBulkSchedulePaymentExceptions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (rows) => {
      const response = await api.post(endpoints.BULK_SCHEDULE_PAYMENT_EXCEPTIONS, rows);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-payment"] });
    },
  });
};

export const useUpdateSchedulePaymentException = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }) => {
      const response = await api.patch(endpoints.UPDATE_SCHEDULE_PAYMENT_EXCEPTION(id), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-payment"] });
    },
  });
};

export const useDeleteSchedulePaymentException = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(endpoints.DELETE_SCHEDULE_PAYMENT_EXCEPTION(id));
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-payment"] });
    },
  });
};

// ─── Daily Bank Balance ────────────────────────────────────────────────────────

/**
 * List daily bank balance snapshots. Supported filters:
 *   - clinic:           clinic code (CSV for multiple)
 *   - netsuite_bank_id: NetSuite bank account id (CSV for multiple)
 *   - date:             exact day (YYYY-MM-DD)
 *   - date_from / date_to
 * No params returns ALL rows (see CustomLimitOffsetPagination).
 */
/**
 * Bank accounts — the registry Daily Bank Balance is keyed on.
 *
 * Supported filters: `clinic` (code, CSV), `entity` (exact name), `status`,
 * `account_type`, `currency`, `has_clinic` (true = clinic-owned only,
 * false = entity-level only, omitted = both).
 *
 * Prefer this over `useClinicBankAccounts` for anything balance-related:
 * that one derives a single active account per clinic from
 * `Clinic.netsuite_bank_balance` and cannot see entity-level or
 * manually-added accounts.
 */
export const useBankAccounts = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["bank-accounts", "list", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_ALL_BANK_ACCOUNTS, filters, {
        clean: true,
      });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

/**
 * Bank lines pulled by the daily bank sync (Meridian), from our own DB.
 *
 * Filters: bank_id (CSV of bank_accounts.id — required), date or
 * date_from/date_to, direction ("deposit" | "withdrawal"), limit.
 * Returns { count, results: [{ id, bank, posted_date, description, amount, … }] }.
 */
export const useBankDailyTransactions = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["bank-daily-transactions", "list", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_BANK_DAILY_TRANSACTIONS, filters, {
        clean: true,
      });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
    enabled: (options.enabled ?? true) && !!filters.bank_id,
  });
};

/**
 * Import a bank's own CSV export (BMO, Desjardins) into bank_daily_transaction.
 * The backend reads the account from the file and skips rows it already holds.
 * With `preview: true` nothing is written and the response adds
 * `lines: [{ posted_date, description, amount, status, new }]`.
 * Returns { bank_account: { id, label }, rows, inserted, skipped, duplicates,
 * date_from, date_to }; a Desjardins file, which covers several accounts, adds
 * `accounts: [{ id, label, inserted, … }]`, `ignored: [{ folio, type, rows, reason }]`
 * and an `account` label on each line.
 */
export const useUploadBankCsv = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ bankType, file, preview = false }) => {
      const formData = new FormData();
      formData.append("bank_type", bankType);
      formData.append("file", file);
      if (preview) formData.append("preview", "true");
      const response = await api.post(endpoints.UPLOAD_BANK_CSV, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },
    onSuccess: (_data, { preview }) => {
      if (!preview) queryClient.invalidateQueries({ queryKey: ["bank-daily-transactions"] });
    },
  });
};

/**
 * Manual deposit ↔ bank matches for one clinic on the Daily Deposits tab.
 * Filters: clinic (code — required), date_from, date_to. Returns a plain list:
 * [{ id, clinic_code, total, created_by_name, created_at,
 *    lines: [{ side: "deposit" | "bank", key, date, amount, description }] }].
 */
export const useDepositMatches = (filters = {}, options = {}) =>
  useQuery({
    queryKey: ["deposit-matches", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.DEPOSIT_MATCHES, filters, { clean: true });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
    enabled: (options.enabled ?? true) && !!filters.clinic,
  });

/**
 * `useDepositMatches` for several clinics at once (Deposit Sheet's Bank
 * columns), one request per clinic under the same cache keys, so a match
 * accepted on Daily Deposits refreshes both pages.
 * Returns { byClinic: { CODE: [match, …] }, isSuccess, isError }.
 */
export const useDepositMatchesForClinics = (clinics = [], range = {}, options = {}) => {
  const { enabled: enabledOption = true } = options;
  const codes = Array.from(new Set((clinics || []).filter(Boolean))).map((c) =>
    String(c).toUpperCase(),
  );
  return useQueries({
    queries: codes.map((clinic) => {
      const filters = { clinic, date_from: range.date_from, date_to: range.date_to };
      return {
        queryKey: ["deposit-matches", filters],
        queryFn: async () => {
          const url = buildUrlWithParams(endpoints.DEPOSIT_MATCHES, filters, { clean: true });
          const response = await api.get(url);
          return response.data;
        },
        enabled: enabledOption && Boolean(range.date_from) && Boolean(range.date_to),
      };
    }),
    combine: (results) => ({
      byClinic: Object.fromEntries(codes.map((c, i) => [c, results[i]?.data ?? []])),
      isSuccess: results.length > 0 && results.every((r) => r.isSuccess),
      isError: results.some((r) => r.isError),
    }),
  });
};

/** Create a match: { clinic, deposits: [{ key, date, amount }], bank: [...] }. */
export const useCreateDepositMatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const response = await api.post(endpoints.DEPOSIT_MATCHES, payload);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["deposit-matches"] }),
  });
};

/**
 * Keys of lines someone unmatched for one clinic (["2026-10-01:collection_visa",
 * "ns-92669", …]). Daily Deposits never auto-matches these again. Filters:
 * clinic (required), date_from, date_to. Under the "deposit-matches" key, so an
 * unmatch refreshes it.
 */
export const useDepositMatchDismissals = (filters = {}, options = {}) =>
  useQuery({
    queryKey: ["deposit-matches", "dismissed", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.DEPOSIT_MATCH_DISMISSED, filters, { clean: true });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
    enabled: (options.enabled ?? true) && !!filters.clinic,
  });

/**
 * Save many matches for one clinic in one request (Accept all, auto-match).
 * Payload: { clinic, auto?, matches: [{ deposits, bank }] } (≤ 500).
 * Returns { created: [match], skipped: [{ index, error }] } — a match whose
 * lines were taken meanwhile is skipped, the rest are saved.
 */
export const useBulkCreateDepositMatches = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const response = await api.post(endpoints.DEPOSIT_MATCHES_BULK, payload);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["deposit-matches"] }),
  });
};

/** Unmatch: deletes the match; its lines become pending again (and are
 * remembered, so Daily Deposits won't auto-match them again). */
export const useDeleteDepositMatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await api.delete(endpoints.DEPOSIT_MATCH(id));
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["deposit-matches"] }),
  });
};

/** Corporate entities that own bank accounts. */
export const useEntities = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["entities", "list", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_ALL_ENTITIES, filters, { clean: true });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

export const useDailyBankBalances = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["daily-bank-balance", "list", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_ALL_DAILY_BANK_BALANCES, filters, {
        clean: true,
      });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

export const useCreateDailyBankBalance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(endpoints.CREATE_DAILY_BANK_BALANCE, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["daily-bank-balance"] });
    },
  });
};

// Create many snapshots in ONE request, with one cache invalidation at the end.
// The NetSuite auto-fill needs this: per-row creates meant a POST per account
// and a full list refetch per POST, so a 27-account day cost 27 writes and 27
// reads. Rows that already exist are skipped server-side, never overwritten.
export const useBulkCreateDailyBankBalance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (items) => {
      const response = await api.post(endpoints.BULK_CREATE_DAILY_BANK_BALANCE, { items });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["daily-bank-balance"] });
    },
  });
};

export const useUpdateDailyBankBalance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }) => {
      const response = await api.patch(endpoints.UPDATE_DAILY_BANK_BALANCE(id), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["daily-bank-balance"] });
    },
  });
};

/**
 * List intercompany transfers. Supported filters:
 *   - clinic:    clinic code (CSV for multiple; matches either side)
 *   - date:      exact day (YYYY-MM-DD)
 *   - date_from / date_to
 *   - status:    pending | approved | rejected
 */
export const useTransfers = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: ["transfers", "list", filters],
    queryFn: async () => {
      const url = buildUrlWithParams(endpoints.GET_ALL_TRANSFERS, filters, { clean: true });
      const response = await api.get(url);
      return response.data;
    },
    ...options,
  });
};

export const useCreateTransfer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post(endpoints.CREATE_TRANSFER, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["transfers"] });
    },
  });
};

export const useUpdateTransfer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }) => {
      const response = await api.patch(endpoints.UPDATE_TRANSFER(id), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["transfers"] });
    },
  });
};

export const useDeleteTransfer = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(endpoints.DELETE_TRANSFER(id));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["transfers"] });
    },
  });
};
